import { CommonModule } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from "@angular/core";
import { Router } from "@angular/router";
import { Subscription } from "rxjs";
import { LucideDynamicIcon } from "@lucide/angular";
import { environment } from "@/environments/environment";
import { backend } from "@/integrations/backend/client";
import type { Tables } from "@/integrations/backend/types";
import {
  resolveLocalizedText,
  type LocalizedTextMap,
} from "@/lib/localizedContent";
import { cn } from "@/lib/utils";
import { AuthService, Profile, User, UserStreak } from "../core/auth.service";
import {
  dailyStreakBonusForStreak,
  isDailyStreakWeekend,
  loadDailyStreakBonusTotals,
} from "../core/daily-streak-bonus";
import { LanguageService } from "../core/language.service";
import { ToastService } from "../core/toast.service";
import { HubFlagComponent } from "../shared/hub-flag.component";
import { LanguageSelectorComponent } from "../shared/language-selector.component";
import {
  badgeClasses,
  buttonClasses,
  cardClasses,
  progressBarStyle,
} from "../shared/ui";
import {
  AdventureIslandBackdropComponent,
  IslandUpgradeArtComponent,
} from "./island-art.component";
import {
  getQuestionBankStationIdsForIsland,
  isQuestionBankStationForIsland,
} from "./question-bank-route";
import {
  getPlaytestProgressKey,
  loadStoredPlaytestProgress,
  type PlaytestProgressSnapshot,
} from "./playtest-progress";
import {
  calculateJourneyScore,
  completedIslandIdsFromProgress,
  currentIslandIdFromProgress,
  filterIslandProgressForJourney,
  filterStationProgressForJourney,
} from "./journey-progress";
import { syncCurrentUserGameProgress } from "./game-progress-sync";
import { VisualIslandComponent } from "./visual-island.component";

const MAP_DEBUG_PREFIX = "[LINA][map]";

function logMapStep(step: string, details?: unknown) {
  if (!environment.production) {
    console.log(`${MAP_DEBUG_PREFIX} ${step}`, details ?? "");
  }
}

interface Island {
  id: string;
  slug: string;
  name: string;
  name_translations?: LocalizedTextMap;
  description: string;
  description_translations?: LocalizedTextMap;
  order_index: number;
  icon: string | null;
  image_url: string | null;
  crew_id?: string | null;
}

interface IslandProgress {
  user_id: string;
  island_id: string;
  score: number;
  completed_at: string;
}

interface StationSummary {
  id: string;
  island_id: string;
  slug?: string | null;
  source_station_id?: string | null;
}

interface StationProgressSummary {
  user_id?: string | null;
  station_id: string;
  current_stage?: number | null;
  stage_points?: number | null;
  station_points: number;
  completed_at?: string | null;
}

type UpgradeKind =
  | "camp"
  | "campfire"
  | "dock"
  | "pierLantern"
  | "garden"
  | "flowerBed"
  | "orchard"
  | "well"
  | "hut"
  | "workshop"
  | "signpost"
  | "bridge"
  | "fountain"
  | "stoneGate"
  | "tower"
  | "library"
  | "observatory"
  | "banner"
  | "lighthouse"
  | "statue"
  | "market"
  | "greenhouse"
  | "treasure"
  | "obelisk";

type UpgradeSize = "tiny" | "small" | "medium" | "large";

interface IslandUpgrade {
  id: string;
  label: string;
  unlockCount: number;
  x: number;
  y: number;
  kind: UpgradeKind;
  size?: UpgradeSize;
  rotate?: number;
}

type UserIslandItem = Tables<"user_island_items">;

interface IslandShopItem {
  key: string;
  label: string;
  description: string;
  kind: UpgradeKind;
  unlockCount: number;
  unlockScore: number;
  size: UpgradeSize;
  defaultX: number;
  defaultY: number;
}

interface IslandAnimal {
  id: string;
  symbol: string;
  label: string;
  x: number;
  y: number;
  unlockCount: number;
  unlockScore: number;
  motion: "wander" | "fly" | "swim" | "bob";
  delay: number;
  motionBounds?: Pick<AnimalMotionConfig, "minX" | "maxX" | "minY" | "maxY">;
  targetSurface?: "land" | "water" | "open";
}

interface AnimalMotionState {
  x: number;
  y: number;
  floatOffset: number;
  targetX: number;
  targetY: number;
  nextTargetAt: number;
}

interface AnimalMotionConfig {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  speed: number;
  minTargetMs: number;
  maxTargetMs: number;
}

interface MapPoint {
  x: number;
  y: number;
}

type JourneyVehicle = "boat" | "lorry";

interface IslandDragState {
  islandId: string;
  offsetX: number;
  offsetY: number;
  startClientX: number;
  startClientY: number;
  hasMoved: boolean;
}

interface ShopPanelDragState {
  offsetX: number;
  offsetY: number;
  width: number;
  height: number;
}

const LOCAL_ISLAND_ITEMS_KEY_PREFIX = "lina:user-island-items:";
const LOCAL_ISLAND_POSITIONS_KEY_PREFIX = "lina:island-map-positions:v2:";
const CREW_INVITE_NOTIFICATION_KEY_PREFIX = "lina:notified-crew-invites:";
const ISLAND_DRAG_THRESHOLD_PX = 5;
const SHOP_PANEL_MARGIN_PX = 8;
const COMPACT_MAP_BREAKPOINT_PX = 1024;
const STAGES_PER_STATION = 3;
const STAGE_TARGET_POINTS = 10;
const ISLAND_POSITIONS = [
  { x: 34, y: 81 },
  { x: 20, y: 63 },
  { x: 31, y: 50 },
  { x: 39, y: 34 },
  { x: 50, y: 26 },
  { x: 64, y: 34 },
  { x: 76, y: 48 },
  { x: 65, y: 61 },
  { x: 57, y: 76 },
  { x: 49, y: 79 },
  { x: 63, y: 84 },
];
const WATER_ISLAND_POSITIONS: Record<string, MapPoint> = {
  "automation-atoll": { x: 76, y: 36 },
  "ethics-isle": { x: 80, y: 54 },
};
const WATER_ROUTE_SOURCE_SLUGS = new Set(["welcome-cove"]);
const WATER_ROUTE_DESTINATION_SLUGS = new Set([
  "automation-atoll",
  "ethics-isle",
]);
const PERSONAL_ISLAND_UPGRADES: IslandUpgrade[] = [
  {
    id: "brazil-rainforest-camp",
    label: "Brazil Rainforest Camp",
    unlockCount: 1,
    x: 24,
    y: 76,
    kind: "camp",
    size: "medium",
  },
  {
    id: "brazil-campfire",
    label: "Brazil Campfire Circle",
    unlockCount: 1,
    x: 31,
    y: 72,
    kind: "campfire",
    size: "small",
  },
  {
    id: "brazil-trail-sign",
    label: "Brazil Trail Sign",
    unlockCount: 1,
    x: 22,
    y: 61,
    kind: "signpost",
    size: "small",
    rotate: -5,
  },
  {
    id: "brazil-flower-bed",
    label: "Brazil Flower Bed",
    unlockCount: 1,
    x: 43,
    y: 82,
    kind: "flowerBed",
    size: "small",
  },
  {
    id: "brazil-river-dock",
    label: "Brazil River Dock",
    unlockCount: 2,
    x: 34,
    y: 58,
    kind: "dock",
    size: "small",
  },
  {
    id: "brazil-lanterns",
    label: "Brazil River Lanterns",
    unlockCount: 2,
    x: 29,
    y: 47,
    kind: "pierLantern",
    size: "small",
  },
  {
    id: "brazil-hidden-treasure",
    label: "Brazil Hidden Treasure",
    unlockCount: 2,
    x: 22,
    y: 51,
    kind: "treasure",
    size: "small",
    rotate: -8,
  },
  {
    id: "brazil-stone-well",
    label: "Brazil Stone Well",
    unlockCount: 2,
    x: 36,
    y: 68,
    kind: "well",
    size: "small",
  },
  {
    id: "dutch-tulip-garden",
    label: "Dutch Tulip Garden",
    unlockCount: 3,
    x: 54,
    y: 73,
    kind: "garden",
    size: "small",
  },
  {
    id: "dutch-orchard-grove",
    label: "Dutch Orchard Grove",
    unlockCount: 3,
    x: 43,
    y: 44,
    kind: "orchard",
    size: "small",
  },
  {
    id: "dutch-canal-fountain",
    label: "Dutch Canal Fountain",
    unlockCount: 3,
    x: 56,
    y: 68,
    kind: "fountain",
    size: "small",
  },
  {
    id: "dutch-path-banners",
    label: "Dutch Path Banners",
    unlockCount: 3,
    x: 45,
    y: 53,
    kind: "banner",
    size: "small",
    rotate: 4,
  },
  {
    id: "dutch-crew-hut",
    label: "Dutch Canal Hut",
    unlockCount: 4,
    x: 55,
    y: 49,
    kind: "hut",
    size: "small",
  },
  {
    id: "dutch-second-windmill",
    label: "Dutch Second Windmill",
    unlockCount: 4,
    x: 46,
    y: 63,
    kind: "hut",
    size: "small",
    rotate: -3,
  },
  {
    id: "dutch-craft-workshop",
    label: "Dutch Craft Workshop",
    unlockCount: 4,
    x: 59,
    y: 59,
    kind: "workshop",
    size: "small",
  },
  {
    id: "dutch-tiny-library",
    label: "Dutch Tiny Library",
    unlockCount: 4,
    x: 50,
    y: 36,
    kind: "library",
    size: "small",
  },
  {
    id: "dutch-training-obelisk",
    label: "Dutch Training Obelisk",
    unlockCount: 4,
    x: 60,
    y: 40,
    kind: "obelisk",
    size: "small",
  },
  {
    id: "swedish-river-bridge",
    label: "Swedish River Bridge",
    unlockCount: 5,
    x: 68,
    y: 59,
    kind: "bridge",
    size: "small",
  },
  {
    id: "swedish-stone-gate",
    label: "Swedish Stone Gate",
    unlockCount: 5,
    x: 74,
    y: 45,
    kind: "stoneGate",
    size: "small",
  },
  {
    id: "swedish-observatory",
    label: "Swedish Observatory Dome",
    unlockCount: 5,
    x: 70,
    y: 33,
    kind: "observatory",
    size: "small",
  },
  {
    id: "swedish-hero-statue",
    label: "Swedish Hero Statue",
    unlockCount: 5,
    x: 63,
    y: 74,
    kind: "statue",
    size: "small",
  },
  {
    id: "swedish-skill-tower",
    label: "Swedish Skill Tower",
    unlockCount: 6,
    x: 73,
    y: 38,
    kind: "tower",
    size: "small",
  },
  {
    id: "swedish-lighthouse",
    label: "Swedish Lighthouse",
    unlockCount: 6,
    x: 76,
    y: 51,
    kind: "lighthouse",
    size: "small",
  },
  {
    id: "swedish-market",
    label: "Swedish Island Market",
    unlockCount: 6,
    x: 55,
    y: 76,
    kind: "market",
    size: "small",
  },
  {
    id: "swedish-greenhouse",
    label: "Swedish Glass Greenhouse",
    unlockCount: 6,
    x: 69,
    y: 72,
    kind: "greenhouse",
    size: "small",
  },
];

const ISLAND_SHOP_ITEMS: IslandShopItem[] = [
  {
    key: "campfire-circle",
    label: "Fogueira viva",
    description: "Um ponto de acampamento animado para a trilha.",
    kind: "campfire",
    unlockCount: 1,
    unlockScore: 0,
    size: "small",
    defaultX: 31,
    defaultY: 72,
  },
  {
    key: "trail-sign",
    label: "Placa da trilha",
    description: "Marca caminhos e conquistas na ilha.",
    kind: "signpost",
    unlockCount: 1,
    unlockScore: 60,
    size: "small",
    defaultX: 22,
    defaultY: 61,
  },
  {
    key: "flower-bed",
    label: "Jardim de flores",
    description: "Flores coloridas para preencher praia e mata.",
    kind: "flowerBed",
    unlockCount: 1,
    unlockScore: 80,
    size: "small",
    defaultX: 30,
    defaultY: 78,
  },
  {
    key: "river-dock",
    label: "Pier de rio",
    description: "Um pequeno pier para áreas próximas da água.",
    kind: "dock",
    unlockCount: 2,
    unlockScore: 150,
    size: "small",
    defaultX: 34,
    defaultY: 58,
  },
  {
    key: "stone-well",
    label: "Poço de pedra",
    description: "Detalhe rústico para vilas e clareiras.",
    kind: "well",
    unlockCount: 2,
    unlockScore: 180,
    size: "small",
    defaultX: 36,
    defaultY: 68,
  },
  {
    key: "tulip-garden",
    label: "Tulipas da Holanda",
    description: "Canteiro pequeno inspirado na Holanda.",
    kind: "garden",
    unlockCount: 3,
    unlockScore: 250,
    size: "small",
    defaultX: 54,
    defaultY: 73,
  },
  {
    key: "windmill-hut",
    label: "Moinho",
    description: "Pequeno moinho para a região holandesa.",
    kind: "hut",
    unlockCount: 3,
    unlockScore: 300,
    size: "small",
    defaultX: 50,
    defaultY: 48,
  },
  {
    key: "swedish-bridge",
    label: "Ponte sueca",
    description: "Ponte curta para cruzar água e trilha.",
    kind: "bridge",
    unlockCount: 4,
    unlockScore: 360,
    size: "small",
    defaultX: 68,
    defaultY: 59,
  },
  {
    key: "colorful-buildings",
    label: "Prédios coloridos",
    description: "Conjunto pequeno inspirado na Suécia.",
    kind: "market",
    unlockCount: 5,
    unlockScore: 450,
    size: "small",
    defaultX: 76,
    defaultY: 56,
  },
  {
    key: "lighthouse",
    label: "Farol",
    description: "Um farol pequeno para o litoral.",
    kind: "lighthouse",
    unlockCount: 6,
    unlockScore: 540,
    size: "small",
    defaultX: 76,
    defaultY: 51,
  },
  {
    key: "orchard-grove",
    label: "Pomar",
    description: "Arvores frutiferas para deixar a ilha mais viva.",
    kind: "orchard",
    unlockCount: 2,
    unlockScore: 120,
    size: "small",
    defaultX: 42,
    defaultY: 76,
  },
  {
    key: "pier-lantern",
    label: "Lanterna do pier",
    description: "Um ponto de luz para areas proximas da agua.",
    kind: "pierLantern",
    unlockCount: 2,
    unlockScore: 170,
    size: "tiny",
    defaultX: 39,
    defaultY: 61,
  },
  {
    key: "victory-banner",
    label: "Bandeira",
    description: "Uma bandeira para marcar conquistas da tripulacao.",
    kind: "banner",
    unlockCount: 3,
    unlockScore: 260,
    size: "small",
    defaultX: 44,
    defaultY: 42,
  },
  {
    key: "island-fountain",
    label: "Fonte",
    description: "Uma fonte decorativa para a praca da ilha.",
    kind: "fountain",
    unlockCount: 3,
    unlockScore: 320,
    size: "small",
    defaultX: 51,
    defaultY: 65,
  },
  {
    key: "training-workshop",
    label: "Oficina",
    description: "Uma pequena oficina para projetos e melhorias.",
    kind: "workshop",
    unlockCount: 4,
    unlockScore: 380,
    size: "small",
    defaultX: 58,
    defaultY: 61,
  },
  {
    key: "stone-gate",
    label: "Portal de pedra",
    description: "Um portal para destacar a entrada da trilha.",
    kind: "stoneGate",
    unlockCount: 4,
    unlockScore: 420,
    size: "small",
    defaultX: 40,
    defaultY: 50,
  },
  {
    key: "treasure-chest",
    label: "Bau do tesouro",
    description: "Um bau para guardar recompensas da aventura.",
    kind: "treasure",
    unlockCount: 5,
    unlockScore: 470,
    size: "small",
    defaultX: 57,
    defaultY: 77,
  },
  {
    key: "learning-library",
    label: "Biblioteca",
    description: "Uma biblioteca pequena para conhecimento da crew.",
    kind: "library",
    unlockCount: 5,
    unlockScore: 500,
    size: "small",
    defaultX: 49,
    defaultY: 38,
  },
  {
    key: "skill-tower",
    label: "Torre",
    description: "Uma torre alta para acompanhar o progresso da ilha.",
    kind: "tower",
    unlockCount: 6,
    unlockScore: 580,
    size: "small",
    defaultX: 64,
    defaultY: 43,
  },
  {
    key: "glass-greenhouse",
    label: "Estufa",
    description: "Uma estufa de vidro para uma area verde especial.",
    kind: "greenhouse",
    unlockCount: 6,
    unlockScore: 620,
    size: "small",
    defaultX: 62,
    defaultY: 72,
  },
  {
    key: "observatory-dome",
    label: "Observatorio",
    description: "Um observatorio para olhar novas ilhas no horizonte.",
    kind: "observatory",
    unlockCount: 6,
    unlockScore: 680,
    size: "small",
    defaultX: 70,
    defaultY: 34,
  },
  {
    key: "crew-statue",
    label: "Estatua",
    description: "Uma estatua para celebrar os exploradores.",
    kind: "statue",
    unlockCount: 6,
    unlockScore: 720,
    size: "small",
    defaultX: 60,
    defaultY: 74,
  },
];

const ISLAND_ANIMALS: IslandAnimal[] = [
  {
    id: "ocean-fish",
    symbol: "🐟",
    label: "Ocean fish",
    x: 8,
    y: 77,
    unlockCount: 1,
    unlockScore: 40,
    motion: "swim",
    delay: -1.1,
    motionBounds: { minX: 2, maxX: 19, minY: 52, maxY: 92 },
  },
  {
    id: "ocean-tropical-fish",
    symbol: "🐠",
    label: "Tropical fish",
    x: 5,
    y: 66,
    unlockCount: 1,
    unlockScore: 40,
    motion: "swim",
    delay: -2.4,
    motionBounds: { minX: 18, maxX: 42, minY: 76, maxY: 96 },
  },
  {
    id: "ocean-turtle",
    symbol: "🐢",
    label: "Sea turtle",
    x: 11,
    y: 72,
    unlockCount: 1,
    unlockScore: 40,
    motion: "swim",
    delay: -0.4,
    motionBounds: { minX: 43, maxX: 62, minY: 79, maxY: 97 },
  },
  {
    id: "ocean-dolphin",
    symbol: "🐬",
    label: "Dolphin",
    x: 6,
    y: 86,
    unlockCount: 1,
    unlockScore: 40,
    motion: "swim",
    delay: -3.2,
    motionBounds: { minX: 73, maxX: 96, minY: 39, maxY: 82 },
  },
  {
    id: "ocean-whale",
    symbol: "🐋",
    label: "Whale",
    x: 12,
    y: 91,
    unlockCount: 1,
    unlockScore: 40,
    motion: "swim",
    delay: -1.8,
    motionBounds: { minX: 62, maxX: 96, minY: 73, maxY: 98 },
  },
  {
    id: "ocean-jellyfish",
    symbol: "🪼",
    label: "Jellyfish",
    x: 4,
    y: 81,
    unlockCount: 1,
    unlockScore: 40,
    motion: "swim",
    delay: -2.8,
    motionBounds: { minX: 70, maxX: 96, minY: 15, maxY: 47 },
  },
  {
    id: "ocean-squid",
    symbol: "🦑",
    label: "Squid",
    x: 88,
    y: 63,
    unlockCount: 1,
    unlockScore: 40,
    motion: "swim",
    delay: -3.8,
    motionBounds: { minX: 80, maxX: 98, minY: 52, maxY: 88 },
  },
  {
    id: "ocean-shark",
    symbol: "🦈",
    label: "Reef shark",
    x: 29,
    y: 92,
    unlockCount: 1,
    unlockScore: 40,
    motion: "swim",
    delay: -4.4,
    motionBounds: { minX: 8, maxX: 38, minY: 86, maxY: 99 },
  },
  {
    id: "ocean-crab",
    symbol: "🦀",
    label: "Shore crab",
    x: 92,
    y: 36,
    unlockCount: 1,
    unlockScore: 40,
    motion: "swim",
    delay: -1.6,
    motionBounds: { minX: 88, maxX: 99, minY: 24, maxY: 52 },
  },
];

const ANIMAL_MOTION_CONFIG: Record<IslandAnimal["motion"], AnimalMotionConfig> =
  {
    wander: {
      minX: 13,
      maxX: 87,
      minY: 18,
      maxY: 91,
      speed: 5.4,
      minTargetMs: 1400,
      maxTargetMs: 3600,
    },
    fly: {
      minX: 10,
      maxX: 90,
      minY: 12,
      maxY: 82,
      speed: 8.8,
      minTargetMs: 1000,
      maxTargetMs: 2500,
    },
    swim: {
      minX: 4,
      maxX: 12,
      minY: 58,
      maxY: 91,
      speed: 6.2,
      minTargetMs: 1100,
      maxTargetMs: 2800,
    },
    bob: {
      minX: 15,
      maxX: 85,
      minY: 18,
      maxY: 88,
      speed: 6.8,
      minTargetMs: 1000,
      maxTargetMs: 2600,
    },
  };
const ANIMAL_MAX_VISIBLE_Y = 92;

function randomBetween(min: number, max: number) {
  return min + Math.random() * (max - min);
}

function getLocalIslandItemsKey(userId: string) {
  return `${LOCAL_ISLAND_ITEMS_KEY_PREFIX}${userId}`;
}

function getLocalIslandPositionsKey(userId: string) {
  return `${LOCAL_ISLAND_POSITIONS_KEY_PREFIX}${userId}`;
}

function isLocalIslandItem(item: UserIslandItem) {
  return item.id.startsWith("local-");
}

function readLocalIslandItems(userId: string): UserIslandItem[] {
  try {
    const savedItems = window.localStorage.getItem(
      getLocalIslandItemsKey(userId),
    );
    if (!savedItems) return [];
    const parsedItems = JSON.parse(savedItems);
    return Array.isArray(parsedItems) ? parsedItems : [];
  } catch (error) {
    console.error("Error reading local island items:", error);
    return [];
  }
}

function saveLocalIslandItems(userId: string, items: UserIslandItem[]) {
  try {
    window.localStorage.setItem(
      getLocalIslandItemsKey(userId),
      JSON.stringify(items),
    );
  } catch (error) {
    console.error("Error saving local island items:", error);
  }
}

function readLocalIslandPositions(userId: string): Record<string, MapPoint> {
  try {
    const savedPositions = window.localStorage.getItem(
      getLocalIslandPositionsKey(userId),
    );
    if (!savedPositions) return {};
    const parsedPositions = JSON.parse(savedPositions);
    if (
      !parsedPositions ||
      typeof parsedPositions !== "object" ||
      Array.isArray(parsedPositions)
    )
      return {};

    return Object.entries(parsedPositions).reduce<Record<string, MapPoint>>(
      (positions, [islandId, value]) => {
        if (!value || typeof value !== "object") return positions;
        const point = value as Partial<MapPoint>;
        if (typeof point.x !== "number" || typeof point.y !== "number")
          return positions;
        positions[islandId] = {
          x: clampIslandCoordinate(point.x),
          y: clampIslandCoordinate(point.y),
        };
        return positions;
      },
      {},
    );
  } catch (error) {
    console.error("Error reading local island positions:", error);
    return {};
  }
}

function saveLocalIslandPositions(
  userId: string,
  positions: Record<string, MapPoint>,
) {
  try {
    window.localStorage.setItem(
      getLocalIslandPositionsKey(userId),
      JSON.stringify(positions),
    );
  } catch (error) {
    console.error("Error saving local island positions:", error);
  }
}

function clearLocalIslandPositions(userId: string) {
  try {
    window.localStorage.removeItem(getLocalIslandPositionsKey(userId));
  } catch (error) {
    console.error("Error clearing local island positions:", error);
  }
}

function createLocalIslandItem(
  userId: string,
  itemKey: string,
  x: number,
  y: number,
): UserIslandItem {
  const createdAt = new Date().toISOString();
  const randomId = crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  return {
    id: `local-${randomId}`,
    user_id: userId,
    item_key: itemKey,
    x,
    y,
    created_at: createdAt,
    updated_at: createdAt,
  } as UserIslandItem;
}

function isIslandItemsPersistenceError(error: unknown) {
  const backendError = error as {
    code?: string;
    message?: string;
    details?: string;
    hint?: string;
  } | null;
  const errorText = [
    backendError?.message,
    backendError?.details,
    backendError?.hint,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return (
    backendError?.code === "42P01" ||
    backendError?.code === "PGRST205" ||
    backendError?.code === "42501" ||
    errorText.includes("user_island_items") ||
    errorText.includes("schema cache") ||
    errorText.includes("row-level security") ||
    errorText.includes("permission denied")
  );
}

function getIslandPosition(index: number, total = 6) {
  if (total <= 1) return ISLAND_POSITIONS[0];
  const routeMaxIndex = ISLAND_POSITIONS.length - 1;
  const levelMaxIndex = Math.max(total - 1, 1);
  const scaledIndex =
    (Math.min(index, levelMaxIndex) / levelMaxIndex) * routeMaxIndex;
  const lowerIndex = Math.floor(scaledIndex);
  const upperIndex = Math.min(Math.ceil(scaledIndex), routeMaxIndex);
  const blend = scaledIndex - lowerIndex;
  const lower = ISLAND_POSITIONS[lowerIndex];
  const upper = ISLAND_POSITIONS[upperIndex];
  return {
    x: lower.x + (upper.x - lower.x) * blend,
    y: lower.y + (upper.y - lower.y) * blend,
  };
}

function baseIslandSlug(slug: string) {
  return slug.replace(/-[0-9a-f]{8}$/i, "");
}

function preferredIslandPosition(
  island: Pick<Island, "slug">,
  index: number,
  total: number,
) {
  return (
    WATER_ISLAND_POSITIONS[baseIslandSlug(island.slug)] ||
    getIslandPosition(index, total)
  );
}

function isBoatRouteSegment(sourceSlug: string, destinationSlug: string) {
  const source = baseIslandSlug(sourceSlug);
  const destination = baseIslandSlug(destinationSlug);
  return (
    WATER_ROUTE_SOURCE_SLUGS.has(source) ||
    WATER_ROUTE_DESTINATION_SLUGS.has(destination)
  );
}

function getShopItemByKey(itemKey: string) {
  return ISLAND_SHOP_ITEMS.find((item) => item.key === itemKey);
}

function isShopItemUnlocked(
  item: IslandShopItem,
  currentLevel: number,
  totalScore: number,
) {
  return currentLevel >= item.unlockCount && totalScore >= item.unlockScore;
}

function clampMapCoordinate(value: number) {
  return Number(Math.min(Math.max(value, 0), 100).toFixed(2));
}

function clampIslandCoordinate(value: number) {
  return Number(Math.min(Math.max(value, 6), 94).toFixed(2));
}

function clampRatio(value: number) {
  if (!Number.isFinite(value)) return 0;
  return Math.min(Math.max(value, 0), 1);
}

function safeCompletedPoints(value: number | null | undefined) {
  const points = Number(value ?? 0);
  return Number.isFinite(points) ? Math.max(0, Math.trunc(points)) : 0;
}

function normalizeStationProgressKey(value?: string | null) {
  return value?.trim().toLowerCase().replace(/-/g, "_") ?? "";
}

function storedStationCompletionScore(
  snapshot: PlaytestProgressSnapshot | null,
) {
  if (!snapshot) return null;

  const completedStages = new Set(snapshot.completedStages ?? []);
  const completed =
    [1, 2, 3].every((stage) => completedStages.has(stage)) ||
    Boolean(snapshot.session?.isComplete);

  if (!completed) return null;

  return safeCompletedPoints(
    snapshot.totalScore ?? snapshot.session?.stationPoints ?? 0,
  );
}

function isPointInsideIslandLand(x: number, y: number) {
  if (x < 7 || x > 93 || y < 6 || y > 100) return false;
  const leftEdge =
    y < 17
      ? 24 - (y - 6) * 0.55
      : y < 36
        ? 18 - (y - 17) * 0.58
        : y < 47
          ? 7 + (y - 36) * 0.82
          : y < 63
            ? 16 - (y - 47) * 0.12
            : y < 89
              ? 14 + (y - 63) * 0.85
              : 36 + (y - 89) * 0.22;
  const rightEdge =
    y < 17
      ? 76 + (y - 6) * 0.55
      : y < 36
        ? 82 + (y - 17) * 0.58
        : y < 47
          ? 93 - (y - 36) * 0.82
          : y < 63
            ? 84 + (y - 47) * 0.12
            : y < 89
              ? 86 - (y - 63) * 0.85
              : 64 - (y - 89) * 0.22;
  return x >= leftEdge && x <= rightEdge;
}

function randomPointInsideIsland(
  config: AnimalMotionConfig,
  allowOpenAir = false,
): MapPoint {
  for (let attempt = 0; attempt < 48; attempt += 1) {
    const point = {
      x: randomBetween(config.minX, config.maxX),
      y: randomBetween(config.minY, config.maxY),
    };

    if (allowOpenAir || isPointInsideIslandLand(point.x, point.y)) {
      return point;
    }
  }

  return { x: randomBetween(35, 65), y: randomBetween(42, 76) };
}

function animalTargetSurface(animal: IslandAnimal) {
  if (animal.targetSurface) return animal.targetSurface;
  if (animal.motion === "swim") return "water";
  if (animal.motion === "fly") return "open";
  return "land";
}

function animalMotionConfig(animal: IslandAnimal): AnimalMotionConfig {
  const config = {
    ...ANIMAL_MOTION_CONFIG[animal.motion],
    ...(animal.motionBounds || {}),
  };
  const maxY = Math.min(config.maxY, ANIMAL_MAX_VISIBLE_Y);

  return {
    ...config,
    maxY,
    minY: Math.min(config.minY, maxY),
  };
}

function isAnimalPointAllowed(animal: IslandAnimal, point: MapPoint) {
  const surface = animalTargetSurface(animal);
  const isLand = isPointInsideIslandLand(point.x, point.y);

  return (
    surface === "open" ||
    (surface === "land" && isLand) ||
    (surface === "water" && !isLand)
  );
}

function nearestAllowedPointForAnimal(
  animal: IslandAnimal,
  config: AnimalMotionConfig,
) {
  const preferred = {
    x: Math.min(Math.max(animal.x, config.minX), config.maxX),
    y: Math.min(Math.max(animal.y, config.minY), config.maxY),
  };
  let bestPoint: MapPoint | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (let y = config.minY; y <= config.maxY; y += 2) {
    for (let x = config.minX; x <= config.maxX; x += 2) {
      const point = { x, y };
      if (!isAnimalPointAllowed(animal, point)) continue;

      const distance = Math.hypot(point.x - preferred.x, point.y - preferred.y);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestPoint = point;
      }
    }
  }

  return bestPoint ?? preferred;
}

function randomPointForAnimal(
  animal: IslandAnimal,
  config: AnimalMotionConfig,
) {
  for (let attempt = 0; attempt < 128; attempt += 1) {
    const point = {
      x: randomBetween(config.minX, config.maxX),
      y: randomBetween(config.minY, config.maxY),
    };

    if (isAnimalPointAllowed(animal, point)) {
      return point;
    }
  }

  return nearestAllowedPointForAnimal(animal, config);
}

function buildRoutePath(points: MapPoint[]) {
  if (points.length === 0) return "";
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;

  const segments = [`M ${points[0].x} ${points[0].y}`];

  for (let index = 0; index < points.length - 1; index += 1) {
    const previous = points[Math.max(0, index - 1)];
    const current = points[index];
    const next = points[index + 1];
    const afterNext = points[Math.min(points.length - 1, index + 2)];
    const controlOne = {
      x: current.x + (next.x - previous.x) / 6,
      y: current.y + (next.y - previous.y) / 6,
    };
    const controlTwo = {
      x: next.x - (afterNext.x - current.x) / 6,
      y: next.y - (afterNext.y - current.y) / 6,
    };

    segments.push(
      `C ${controlOne.x.toFixed(2)} ${controlOne.y.toFixed(2)}, ${controlTwo.x.toFixed(2)} ${controlTwo.y.toFixed(2)}, ${next.x} ${next.y}`,
    );
  }

  return segments.join(" ");
}

function routeSegment(points: MapPoint[], index: number) {
  const previous = points[Math.max(0, index - 1)];
  const current = points[index];
  const next = points[index + 1];
  const afterNext = points[Math.min(points.length - 1, index + 2)];

  return {
    current,
    next,
    controlOne: {
      x: current.x + (next.x - previous.x) / 6,
      y: current.y + (next.y - previous.y) / 6,
    },
    controlTwo: {
      x: next.x - (afterNext.x - current.x) / 6,
      y: next.y - (afterNext.y - current.y) / 6,
    },
  };
}

function cubicRoutePoint(
  start: MapPoint,
  controlOne: MapPoint,
  controlTwo: MapPoint,
  end: MapPoint,
  progress: number,
) {
  const t = clampRatio(progress);
  const inverse = 1 - t;

  return {
    x:
      inverse ** 3 * start.x +
      3 * inverse ** 2 * t * controlOne.x +
      3 * inverse * t ** 2 * controlTwo.x +
      t ** 3 * end.x,
    y:
      inverse ** 3 * start.y +
      3 * inverse ** 2 * t * controlOne.y +
      3 * inverse * t ** 2 * controlTwo.y +
      t ** 3 * end.y,
  };
}

function routePointAtIndex(points: MapPoint[], routeIndex: number) {
  if (points.length === 0) return { x: 0, y: 0 };
  if (points.length === 1) return points[0];

  const clampedIndex = Math.min(Math.max(routeIndex, 0), points.length - 1);
  const segmentIndex = Math.min(Math.floor(clampedIndex), points.length - 2);
  const segmentProgress = clampedIndex - segmentIndex;
  const { current, controlOne, controlTwo, next } = routeSegment(
    points,
    segmentIndex,
  );

  return cubicRoutePoint(
    current,
    controlOne,
    controlTwo,
    next,
    segmentProgress,
  );
}

@Component({
  selector: "app-island-map",
  standalone: true,
  imports: [
    CommonModule,
    HubFlagComponent,
    LanguageSelectorComponent,
    LucideDynamicIcon,
    VisualIslandComponent,
    AdventureIslandBackdropComponent,
    IslandUpgradeArtComponent,
  ],
  template: `
    <div
      *ngIf="loading"
      class="min-h-screen flex items-center justify-center ocean-gradient"
    >
      <svg
        lucideIcon="compass"
        class="h-12 w-12 animate-spin text-primary-foreground"
      ></svg>
    </div>

    <div *ngIf="!loading" class="relative min-h-screen overflow-x-hidden">
      <div class="absolute inset-0 ocean-gradient"></div>
      <div class="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          class="absolute left-[8%] top-[16%] h-16 w-32 rounded-full bg-white/30 blur-xl animate-float"
        ></div>
        <div
          class="absolute right-[6%] top-[28%] h-14 w-40 rounded-full bg-white/20 blur-xl animate-wave"
        ></div>
        <div
          class="absolute bottom-[12%] left-[12%] h-10 w-52 rounded-full bg-white/20 blur-lg"
        ></div>
        <div
          class="absolute bottom-[20%] right-[14%] h-12 w-44 rounded-full bg-white/20 blur-lg"
        ></div>
      </div>

      <header
        class="sticky top-0 z-50 bg-background/60 backdrop-blur-md border-b border-border/50"
      >
        <div class="container max-w-7xl mx-auto px-4 py-3">
          <div
            class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
          >
            <div class="flex min-w-0 items-center gap-3">
              <div class="p-2 rounded-full tropical-gradient shadow-lg">
                <svg
                  lucideIcon="map-pin"
                  class="h-5 w-5 text-primary-foreground"
                ></svg>
              </div>
              <div class="min-w-0">
                <div class="flex min-w-0 flex-wrap items-baseline gap-x-2">
                  <h1 class="text-lg font-bold text-foreground">LINA</h1>
                  <span
                    class="max-w-[42vw] truncate text-[10px] font-semibold uppercase text-muted-foreground sm:max-w-none sm:text-xs"
                  >
                    {{ language.t("brand.fullName") }}
                  </span>
                </div>
                <p
                  class="flex items-center gap-1.5 text-xs text-muted-foreground"
                >
                  <app-hub-flag [hub]="profile?.hub" size="sm" />
                  <span>{{
                    language.t("map.welcome", {
                      name: profile?.display_name || language.t("map.explorer"),
                    })
                  }}</span>
                </p>
              </div>
            </div>
            <div
              class="flex w-full min-w-0 touch-pan-x items-center gap-1 overflow-x-auto overscroll-x-contain pb-2 sm:w-auto sm:overflow-visible sm:pb-0 [&>button]:shrink-0"
            >
              <app-language-selector
                [compact]="true"
                className="hidden border-primary/20 bg-background/70 md:flex"
              />
              <button
                *ngIf="isAdmin || isCrewOwner"
                [class]="buttonClasses('ghost', 'icon', 'hover:bg-accent/10')"
                (click)="router.navigateByUrl('/admin/islands')"
                [title]="
                  isAdmin
                    ? language.t('map.globalAdmin')
                    : language.t('map.crewAdmin')
                "
              >
                <svg lucideIcon="shield" class="h-5 w-5 text-primary"></svg>
              </button>
              <button
                [class]="buttonClasses('ghost', 'icon', 'hover:bg-accent/10')"
                (click)="router.navigateByUrl('/self-assessment')"
                [title]="language.t('map.selfAssessment')"
              >
                <svg lucideIcon="clipboard-list" class="h-5 w-5"></svg>
              </button>
              <button
                [class]="buttonClasses('ghost', 'icon', 'hover:bg-primary/10')"
                (click)="shopOpen = !shopOpen"
                [title]="language.t('map.itemShop')"
              >
                <svg lucideIcon="store" class="h-5 w-5"></svg>
              </button>
              <button
                [class]="
                  buttonClasses('ghost', 'icon', 'relative hover:bg-primary/10')
                "
                (click)="router.navigateByUrl('/crew')"
                [title]="language.t('map.crew')"
              >
                <svg lucideIcon="users" class="h-5 w-5"></svg>
                <span
                  *ngIf="pendingCrewInvitationCount > 0"
                  class="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-destructive px-1 text-[10px] font-bold leading-none text-destructive-foreground shadow"
                >
                  {{ pendingCrewInvitationCount }}
                </span>
              </button>
              <button
                [class]="
                  buttonClasses('ghost', 'icon', 'hover:bg-secondary/10')
                "
                (click)="router.navigateByUrl('/achievements')"
              >
                <svg lucideIcon="trophy" class="h-5 w-5"></svg>
              </button>
              <button
                [class]="
                  buttonClasses('ghost', 'icon', 'hover:bg-secondary/10')
                "
                (click)="router.navigateByUrl('/winner')"
                [title]="language.t('winner.title')"
              >
                <svg lucideIcon="crown" class="h-5 w-5"></svg>
              </button>
              <button
                [class]="buttonClasses('ghost', 'icon', 'hover:bg-primary/10')"
                (click)="router.navigateByUrl('/tutorial')"
                title="Review tutorial"
              >
                <svg lucideIcon="compass" class="h-5 w-5"></svg>
              </button>
              <button
                [class]="buttonClasses('ghost', 'icon', 'hover:bg-muted')"
                (click)="router.navigateByUrl('/rules')"
                title="Game rules"
              >
                <svg lucideIcon="book-open" class="h-5 w-5"></svg>
              </button>
              <button
                [class]="buttonClasses('ghost', 'icon', 'hover:bg-muted')"
                (click)="router.navigateByUrl('/settings')"
              >
                <svg lucideIcon="settings" class="h-5 w-5"></svg>
              </button>
              <button
                [class]="
                  buttonClasses('ghost', 'icon', 'hover:bg-destructive/10')
                "
                (click)="handleLogout()"
              >
                <svg lucideIcon="log-out" class="h-5 w-5"></svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      <section
        *ngIf="pendingCrewInvitationCount > 0"
        class="relative z-40 mx-auto max-w-7xl px-4 pt-4"
      >
        <div
          [class]="
            cardClasses(
              'flex flex-col gap-3 border-2 border-secondary/50 bg-card/95 p-3 shadow-xl backdrop-blur-sm sm:flex-row sm:items-center sm:justify-between'
            )
          "
        >
          <div class="flex min-w-0 items-start gap-3">
            <div
              class="rounded-full bg-secondary/20 p-2 text-secondary-foreground"
            >
              <svg lucideIcon="mail" class="h-5 w-5"></svg>
            </div>
            <div class="min-w-0">
              <p class="text-sm font-bold text-foreground">
                {{ language.t("map.pendingCrewInviteTitle") }}
              </p>
              <p class="text-xs leading-5 text-muted-foreground">
                {{
                  language.t("map.pendingCrewInviteDescription", {
                    count: pendingCrewInvitationCount,
                  })
                }}
              </p>
            </div>
          </div>
          <button
            [class]="buttonClasses('default', 'sm', 'shrink-0')"
            (click)="router.navigateByUrl('/crew')"
          >
            <svg lucideIcon="users" class="h-4 w-4"></svg>
            {{ language.t("map.reviewCrewInvite") }}
          </button>
        </div>
      </section>

      <section class="relative z-30 mx-auto max-w-3xl px-4 py-4 lg:hidden">
        <div
          [class]="
            cardClasses(
              'border-2 border-primary/20 bg-card/95 p-4 shadow-xl backdrop-blur-sm'
            )
          "
        >
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <p class="text-xs font-semibold uppercase text-muted-foreground">
                {{ language.t("map.yourJourney") }}
              </p>
              <h2 class="mt-1 text-xl font-bold leading-tight text-foreground">
                {{ language.t("map.mapTitle") }}
              </h2>
              <p class="mt-1 text-sm text-muted-foreground">
                {{ language.t("map.mapSubtitle", { count: islands.length }) }}
              </p>
            </div>
            <button
              [class]="buttonClasses('outline', 'icon', 'shrink-0')"
              (click)="shopOpen = !shopOpen"
              [title]="language.t('map.itemShop')"
            >
              <svg lucideIcon="store" class="h-5 w-5"></svg>
            </button>
          </div>

          <div class="mt-4">
            <div
              class="mb-1 flex justify-between text-xs text-muted-foreground"
            >
              <span>{{ language.t("map.progress") }}</span>
              <span>{{ completedIslandIds.size }}/{{ islands.length }}</span>
            </div>
            <div
              class="relative h-2.5 w-full overflow-hidden rounded-full bg-secondary"
            >
              <div
                class="h-full w-full flex-1 bg-primary transition-all"
                [ngStyle]="progressBarStyle(progressPercentage)"
              ></div>
            </div>
          </div>

          <div class="mt-4 grid grid-cols-3 gap-2">
            <div class="rounded-md bg-primary/10 p-2 text-center">
              <p class="text-lg font-bold text-primary">{{ playerLevel }}</p>
              <p class="text-[10px] leading-tight text-muted-foreground">
                {{ language.t("map.currentLevel") }}
              </p>
            </div>
            <div class="rounded-md bg-muted/70 p-2 text-center">
              <p class="text-lg font-bold text-foreground">{{ totalScore }}</p>
              <p class="text-[10px] leading-tight text-muted-foreground">
                {{ language.t("common.points") }}
              </p>
            </div>
            <div class="rounded-md bg-orange-50 p-2 text-center">
              <p
                class="flex items-center justify-center gap-1 text-lg font-bold text-orange-500"
              >
                <svg
                  lucideIcon="flame"
                  class="h-4 w-4 fill-orange-400 text-orange-500"
                ></svg>
                {{ streak?.current_streak || 0 }}
              </p>
              <p class="text-[10px] leading-tight text-muted-foreground">
                {{ language.t("map.streak") }}
              </p>
            </div>
          </div>

          <div
            *ngIf="dailyStreakBonusPoints > 0 || nextDailyStreakBonus > 0"
            class="mt-4 rounded-md border border-orange-200 bg-orange-50/80 p-3 text-xs leading-snug text-orange-700"
          >
            <div class="flex items-center gap-1 font-semibold">
              <svg
                lucideIcon="flame"
                class="h-3.5 w-3.5 fill-orange-400 text-orange-500"
              ></svg>
              <span>{{
                language.t("map.streakBonusTotal", {
                  points: dailyStreakBonusPoints,
                })
              }}</span>
            </div>
            <p class="mt-1 text-orange-700/80">
              {{
                language.t("map.streakBonusNext", {
                  points: nextDailyStreakBonus,
                })
              }}
            </p>
          </div>
        </div>

        <div class="mt-4 space-y-2">
          <button
            *ngFor="let island of islands; let index = index"
            type="button"
            [class]="mobileIslandRowClass(island, index)"
            (click)="handleIslandClick(island, index)"
          >
            <span [class]="mobileIslandNumberClass(island, index)">
              <svg
                *ngIf="completedIslandIds.has(island.id)"
                lucideIcon="circle-check-big"
                class="h-5 w-5"
              ></svg>
              <svg
                *ngIf="
                  !completedIslandIds.has(island.id) &&
                  island.id === currentIslandId
                "
                lucideIcon="play"
                class="h-5 w-5 fill-current"
              ></svg>
              <svg
                *ngIf="
                  !completedIslandIds.has(island.id) &&
                  island.id !== currentIslandId &&
                  isIslandLocked(index)
                "
                lucideIcon="lock"
                class="h-5 w-5"
              ></svg>
              <span
                *ngIf="
                  !completedIslandIds.has(island.id) &&
                  island.id !== currentIslandId &&
                  !isIslandLocked(index)
                "
                >{{ index + 1 }}</span
              >
            </span>
            <span class="min-w-0 flex-1 text-left">
              <span class="block truncate text-sm font-bold text-foreground">
                {{ island.name }}
              </span>
              <span class="mt-0.5 block text-xs text-muted-foreground">
                {{ mobileIslandStatusLabel(island, index) }}
              </span>
            </span>
            <svg
              lucideIcon="chevron-right"
              class="h-5 w-5 shrink-0 text-muted-foreground"
            ></svg>
          </button>
        </div>
      </section>

      <aside
        [class]="
          cardClasses(
            'hidden lg:block absolute top-20 left-4 z-40 p-4 bg-card/90 backdrop-blur-sm shadow-xl border-2 border-primary/20 w-48'
          )
        "
      >
        <h3
          class="text-sm font-semibold text-foreground mb-3 flex items-center gap-2"
        >
          <svg lucideIcon="compass" class="h-4 w-4 text-primary"></svg>
          {{ language.t("map.yourJourney") }}
        </h3>
        <div class="space-y-3">
          <div>
            <div
              class="flex justify-between text-xs text-muted-foreground mb-1"
            >
              <span>{{ language.t("map.progress") }}</span>
              <span>{{ completedIslandIds.size }}/{{ islands.length }}</span>
            </div>
            <div
              class="relative h-2 w-full overflow-hidden rounded-full bg-secondary"
            >
              <div
                class="h-full w-full flex-1 bg-primary transition-all"
                [ngStyle]="progressBarStyle(progressPercentage)"
              ></div>
            </div>
          </div>
          <div class="rounded-md border border-primary/20 bg-primary/10 p-2">
            <div class="flex items-center justify-between gap-2">
              <span class="text-xs font-semibold text-muted-foreground">{{
                language.t("map.currentLevel")
              }}</span>
              <span class="text-lg font-bold text-primary">{{
                playerLevel
              }}</span>
            </div>
            <p class="mt-1 text-[10px] leading-snug text-muted-foreground">
              {{
                language.t("map.currentLevelDescription", {
                  level: playerLevel,
                  count: islands.length,
                })
              }}
            </p>
          </div>
          <div
            class="flex items-center justify-between pt-2 border-t border-border"
          >
            <div class="text-center">
              <p class="text-xl font-bold text-primary">{{ totalScore }}</p>
              <p class="text-[10px] text-muted-foreground">
                {{ language.t("common.points") }}
              </p>
            </div>
            <div class="text-center">
              <p
                class="flex items-center justify-center gap-1 text-xl font-bold text-orange-500"
              >
                <svg
                  lucideIcon="flame"
                  class="h-4 w-4 fill-orange-400 text-orange-500"
                ></svg>
                {{ streak?.current_streak || 0 }}
              </p>
              <p class="text-[10px] text-muted-foreground">
                {{ language.t("map.streak") }}
              </p>
            </div>
            <div class="text-center">
              <p class="text-xl font-bold text-accent">
                {{ completedIslandIds.size }}
              </p>
              <p class="text-[10px] text-muted-foreground">
                {{ language.t("common.completed") }}
              </p>
            </div>
          </div>
          <div class="rounded-md bg-muted/60 p-2">
            <div class="flex items-center justify-between text-xs">
              <span class="font-medium text-foreground">{{
                language.t("map.islandItems")
              }}</span>
              <span class="text-muted-foreground"
                >{{ placedItems.length }}/{{ shopItems.length }}</span
              >
            </div>
            <p
              *ngIf="nextUpgrade"
              class="mt-1 text-[10px] leading-snug text-muted-foreground"
            >
              {{
                language.t("map.nextUpgrade", {
                  label: nextUpgrade.label,
                  count: nextUpgrade.unlockCount,
                })
              }}
            </p>
          </div>
          <div
            *ngIf="dailyStreakBonusPoints > 0 || nextDailyStreakBonus > 0"
            class="rounded-md border border-orange-200 bg-orange-50/80 p-2 text-[10px] leading-snug text-orange-700"
          >
            <div class="flex items-center gap-1 font-semibold">
              <svg
                lucideIcon="flame"
                class="h-3.5 w-3.5 fill-orange-400 text-orange-500"
              ></svg>
              <span>{{
                language.t("map.streakBonusTotal", {
                  points: dailyStreakBonusPoints,
                })
              }}</span>
            </div>
            <p class="mt-1 text-orange-700/80">
              {{
                language.t("map.streakBonusNext", {
                  points: nextDailyStreakBonus,
                })
              }}
            </p>
          </div>
        </div>
      </aside>

      <aside
        [class]="
          cardClasses(
            'hidden xl:block absolute top-20 right-4 z-40 p-3 bg-card/90 backdrop-blur-sm shadow-xl border-2 border-primary/20'
          )
        "
      >
        <h4 class="text-xs font-semibold text-foreground mb-2">
          {{ language.t("map.legend") }}
        </h4>
        <div class="space-y-1.5 text-[10px]">
          <div class="flex items-center gap-2">
            <svg lucideIcon="ship" class="h-3.5 w-3.5 text-primary"></svg>
            <span class="text-muted-foreground">{{
              language.t("map.yourShip")
            }}</span>
          </div>
          <div class="flex items-center gap-2">
            <div class="w-3 h-3 rounded-full bg-primary animate-pulse"></div>
            <span class="text-muted-foreground">{{
              language.t("common.current")
            }}</span>
          </div>
          <div class="flex items-center gap-2">
            <div class="w-3 h-3 rounded-full bg-accent"></div>
            <span class="text-muted-foreground">{{
              language.t("common.completed")
            }}</span>
          </div>
          <div class="flex items-center gap-2">
            <div class="w-3 h-3 rounded-full bg-muted opacity-50"></div>
            <span class="text-muted-foreground">{{
              language.t("common.locked")
            }}</span>
          </div>
        </div>
      </aside>

      <aside
        *ngIf="shopOpen"
        data-map-ui-control="true"
        data-shop-panel="true"
        class="fixed z-50 w-[20rem] max-w-[calc(100vw-2rem)] animate-in fade-in max-sm:w-auto"
        [ngStyle]="shopPanelStyle"
        (document:pointermove)="handleShopPanelDragMove($event)"
        (document:pointerup)="handleShopPanelDragEnd()"
        (document:pointercancel)="handleShopPanelDragEnd()"
        (wheel)="$event.stopPropagation()"
        (touchmove)="$event.stopPropagation()"
      >
        <div
          [class]="
            cardClasses(
              'flex max-h-[78vh] flex-col overflow-hidden border-2 border-primary/20 bg-card/95 shadow-2xl backdrop-blur-sm'
            )
          "
        >
          <div
            class="shrink-0 cursor-move touch-none select-none border-b border-border/70 p-3"
            [title]="language.t('map.dragShopPanel')"
            (pointerdown)="handleShopPanelDragStart($event)"
          >
            <div class="flex items-center justify-between gap-3">
              <div>
                <h3
                  class="flex items-center gap-2 text-sm font-bold text-foreground"
                >
                  <svg lucideIcon="store" class="h-4 w-4 text-primary"></svg>
                  {{ language.t("map.shopTitle") }}
                  <svg
                    lucideIcon="grip"
                    class="h-3.5 w-3.5 text-muted-foreground"
                    aria-hidden="true"
                  ></svg>
                </h3>
                <p class="mt-1 text-[11px] leading-snug text-muted-foreground">
                  {{ language.t("map.shopDescription") }}
                </p>
                <p
                  *ngIf="!isIslandItemsRemoteAvailable"
                  class="mt-2 rounded-md border border-amber-300/60 bg-amber-100/80 px-2 py-1 text-[10px] font-medium leading-snug text-amber-900"
                >
                  {{ language.t("map.localSaveNotice") }}
                </p>
              </div>
              <div class="rounded-md bg-muted px-2 py-1 text-right">
                <p
                  class="flex items-center gap-1 text-xs font-bold text-foreground"
                >
                  <svg
                    lucideIcon="coins"
                    class="h-3.5 w-3.5 text-secondary"
                  ></svg>
                  {{ totalScore }}
                </p>
                <p class="text-[10px] text-muted-foreground">
                  {{ language.t("common.points").toLowerCase() }}
                </p>
              </div>
            </div>
          </div>

          <div
            class="min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-contain p-3 pr-2"
          >
            <div
              *ngFor="let item of shopItems"
              class="rounded-md border border-border bg-background/70 p-2 transition"
            >
              <div class="flex gap-2">
                <div class="h-12 w-12 shrink-0 rounded-md bg-muted/70 p-1">
                  <app-island-upgrade-art [kind]="item.kind" />
                </div>
                <div class="min-w-0 flex-1">
                  <div class="flex items-start justify-between gap-2">
                    <div>
                      <p
                        class="text-xs font-bold leading-tight text-foreground"
                      >
                        {{ shopItemLabel(item) }}
                      </p>
                      <p
                        class="mt-0.5 text-[10px] leading-snug text-muted-foreground"
                      >
                        {{ shopItemDescription(item) }}
                      </p>
                    </div>
                    <svg
                      *ngIf="!shopItemUnlocked(item)"
                      lucideIcon="lock"
                      class="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                    ></svg>
                  </div>
                  <div class="mt-2 flex items-center justify-between gap-2">
                    <span class="text-[10px] text-muted-foreground">
                      {{
                        language.t("map.unlockRequirement", {
                          level: item.unlockCount,
                          points: item.unlockScore,
                        })
                      }}
                    </span>
                    <div class="flex shrink-0 items-center gap-1">
                      <button
                        [class]="
                          buttonClasses(
                            'ghost',
                            'icon',
                            'hidden h-7 w-7 hover:bg-primary/10 lg:inline-flex'
                          )
                        "
                        [disabled]="!shopItemUnlocked(item) || savingIslandItem"
                        [title]="language.t('map.placeItem')"
                        (click)="selectShopItem(item)"
                      >
                        <svg
                          lucideIcon="mouse-pointer-2"
                          class="h-3.5 w-3.5"
                        ></svg>
                      </button>
                      <button
                        [class]="
                          buttonClasses('outline', 'sm', 'h-7 px-2 text-[10px]')
                        "
                        [disabled]="!shopItemUnlocked(item) || savingIslandItem"
                        [title]="language.t('map.addItem')"
                        (click)="addShopItemToIsland(item)"
                      >
                        <svg lucideIcon="plus" class="mr-1 h-3 w-3"></svg>
                        {{ language.t("map.add") }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="shrink-0 border-t border-border/70 p-3">
            <div class="mb-2 flex items-center justify-between">
              <p class="text-xs font-bold text-foreground">
                {{ language.t("map.placedItems") }}
              </p>
              <span class="text-[10px] text-muted-foreground">{{
                placedItems.length
              }}</span>
            </div>
            <div class="max-h-28 space-y-1 overflow-y-auto">
              <p
                *ngIf="placedItems.length === 0"
                class="text-[11px] text-muted-foreground"
              >
                {{ language.t("map.noPlacedItems") }}
              </p>
              <div
                *ngFor="let item of placedItems"
                class="flex items-center justify-between gap-2 rounded-md bg-muted/60 px-2 py-1"
              >
                <span
                  class="min-w-0 flex-1 truncate text-[11px] font-medium text-foreground"
                  >{{ placedItemLabel(item) }}</span
                >
                <button
                  [class]="
                    buttonClasses(
                      'ghost',
                      'icon',
                      'h-6 w-6 text-muted-foreground hover:text-primary'
                    )
                  "
                  [disabled]="savingIslandItem"
                  [title]="language.t('map.moveItem')"
                  (click)="selectPlacedItem(item)"
                >
                  <svg lucideIcon="move" class="h-3.5 w-3.5"></svg>
                </button>
                <button
                  [class]="
                    buttonClasses(
                      'ghost',
                      'icon',
                      'h-6 w-6 text-muted-foreground hover:text-destructive'
                    )
                  "
                  [disabled]="savingIslandItem"
                  [title]="language.t('map.deleteItem')"
                  (click)="deletePlacedItem(item)"
                >
                  <svg lucideIcon="trash-2" class="h-3.5 w-3.5"></svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </aside>

      <div class="fixed bottom-20 right-4 z-50 hidden flex-col gap-1.5 lg:flex">
        <div
          [class]="
            cardClasses(
              'p-1.5 bg-card/90 backdrop-blur-sm shadow-lg border border-primary/20'
            )
          "
        >
          <div class="flex flex-col gap-0.5">
            <button
              [class]="
                buttonClasses('ghost', 'icon', 'h-7 w-7 hover:bg-primary/10')
              "
              (click)="handleZoomIn()"
              [disabled]="zoom >= maxZoom"
              [title]="language.t('common.zoomIn')"
            >
              <svg lucideIcon="zoom-in" class="h-3.5 w-3.5"></svg>
            </button>
            <div
              class="text-center text-[10px] font-medium text-muted-foreground"
            >
              {{ zoom * 100 | number: "1.0-0" }}%
            </div>
            <button
              [class]="
                buttonClasses('ghost', 'icon', 'h-7 w-7 hover:bg-primary/10')
              "
              (click)="handleZoomOut()"
              [disabled]="zoom <= minZoom"
              [title]="language.t('common.zoomOut')"
            >
              <svg lucideIcon="zoom-out" class="h-3.5 w-3.5"></svg>
            </button>
            <div class="border-t border-border my-0.5"></div>
            <button
              [class]="
                buttonClasses('ghost', 'icon', 'h-7 w-7 hover:bg-primary/10')
              "
              (click)="handleReset()"
              [title]="language.t('common.resetView')"
            >
              <svg lucideIcon="rotate-ccw" class="h-3.5 w-3.5"></svg>
            </button>
            <button
              [class]="
                buttonClasses('ghost', 'icon', 'h-7 w-7 hover:bg-primary/10')
              "
              (click)="resetIslandLayout()"
              [title]="language.t('map.resetIslandLayout')"
            >
              <svg lucideIcon="route" class="h-3.5 w-3.5"></svg>
            </button>
          </div>
        </div>
      </div>

      <div
        class="relative mx-auto hidden h-[820px] w-full max-w-7xl px-2 pt-8 md:h-[980px] md:px-10 lg:block"
        [ngClass]="
          placementMode
            ? 'cursor-crosshair'
            : isDragging
              ? 'cursor-grabbing'
              : 'cursor-grab'
        "
        (mousedown)="handleMouseDown($event)"
        (mousemove)="handleMouseMove($event)"
        (mouseup)="handleMouseUp()"
        (mouseleave)="handleMouseLeave()"
        (wheel)="handleWheel($event)"
      >
        <div
          #mapStage
          class="relative mx-auto h-full max-h-[1040px] w-full max-w-[1050px]"
          [style.transform]="stageTransform"
          [style.transform-origin]="'center center'"
          [style.transition]="isDragging ? 'none' : 'transform 0.2s ease-out'"
          (click)="handleMapPlacementClick($event)"
        >
          <div
            class="absolute left-[5%] top-[10%] h-16 w-32 rounded-full bg-white/35 blur-xl"
          ></div>
          <div
            class="absolute right-[3%] top-[21%] h-14 w-40 rounded-full bg-white/25 blur-xl"
          ></div>
          <div
            class="absolute bottom-[11%] left-[8%] h-10 w-52 rounded-full bg-white/20 blur-lg"
          ></div>
          <div
            class="absolute bottom-[19%] right-[11%] h-12 w-44 rounded-full bg-white/20 blur-lg"
          ></div>

          <app-adventure-island-backdrop
            [routeProgress]="routeProgress"
          ></app-adventure-island-backdrop>

          <svg
            *ngIf="islandRoutePath"
            class="pointer-events-none absolute inset-0 z-[16] h-full w-full overflow-visible"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path [attr.d]="islandRoutePath" class="island-trail-foundation" />
            <path [attr.d]="islandRoutePath" class="island-trail-sand" />
            <path [attr.d]="islandRoutePath" class="island-trail-dashes" />
          </svg>

          <div
            *ngFor="let animal of visibleAnimals"
            class="pointer-events-none absolute z-[18] text-2xl drop-shadow-lg md:text-3xl"
            [ngClass]="animalClass(animal)"
            [style.left.%]="animalPosition(animal).x"
            [style.top.%]="animalPosition(animal).y"
            [title]="animal.label"
            [style.animationDelay]="animal.delay + 's'"
          >
            <span
              class="fauna-sprite block -translate-x-1/2 -translate-y-1/2"
              >{{ animal.symbol }}</span
            >
          </div>

          <div
            *ngFor="let upgrade of unlockedUpgrades; let index = index"
            class="absolute z-20"
            [ngClass]="personalUpgradeSizeClass(upgrade.size)"
            [style.left.%]="upgrade.x"
            [style.top.%]="upgrade.y"
            [title]="upgrade.label + ' unlocked'"
          >
            <div
              class="h-full w-full -translate-x-1/2 -translate-y-1/2"
              [style.rotate]="(upgrade.rotate || 0) + 'deg'"
            >
              <app-island-upgrade-art [kind]="upgrade.kind" />
            </div>
          </div>

          <div
            *ngFor="let item of placedItems"
            data-placed-item="true"
            class="absolute z-[35]"
            [ngClass]="playerItemSizeClass(placedItemSize(item))"
            [style.left.%]="item.x"
            [style.top.%]="item.y"
          >
            <div
              class="relative h-full w-full -translate-x-1/2 -translate-y-1/2"
            >
              <button
                type="button"
                class="block h-full w-full rounded-full focus:outline-none"
                [ngClass]="
                  selectedPlacedItemId === item.id
                    ? 'ring-2 ring-primary ring-offset-2 ring-offset-background'
                    : ''
                "
                [title]="
                  placedItemLabel(item) + ': ' + language.t('map.clickMoveItem')
                "
                (click)="selectPlacedItem(item); $event.stopPropagation()"
              >
                <span class="block h-full w-full drop-shadow-xl">
                  <app-island-upgrade-art [kind]="placedItemKind(item)" />
                </span>
              </button>
              <ng-container *ngIf="selectedPlacedItemId === item.id">
                <button
                  type="button"
                  data-map-ui-control="true"
                  class="absolute -left-4 -top-4 grid h-7 w-7 place-items-center rounded-full border border-primary/40 bg-background/95 text-primary shadow-lg transition hover:bg-primary hover:text-primary-foreground"
                  [attr.aria-label]="language.t('map.moveItem')"
                  [title]="language.t('map.moveItem')"
                  (click)="selectPlacedItem(item); $event.stopPropagation()"
                >
                  <svg lucideIcon="move" class="h-3.5 w-3.5"></svg>
                </button>
                <button
                  type="button"
                  data-map-ui-control="true"
                  class="absolute -right-4 -top-4 grid h-7 w-7 place-items-center rounded-full border border-destructive/40 bg-background/95 text-destructive shadow-lg transition hover:bg-destructive hover:text-destructive-foreground disabled:cursor-not-allowed disabled:opacity-60"
                  [disabled]="savingIslandItem"
                  [attr.aria-label]="language.t('map.deleteItem')"
                  [title]="language.t('map.deleteItem')"
                  (click)="deletePlacedItem(item); $event.stopPropagation()"
                >
                  <svg lucideIcon="trash-2" class="h-3.5 w-3.5"></svg>
                </button>
              </ng-container>
            </div>
          </div>

          <div
            *ngIf="placementMode"
            class="absolute inset-0 z-[34] cursor-crosshair"
            (click)="handleMapPlacementClick($event); $event.stopPropagation()"
          ></div>

          <div
            *ngIf="placementMode"
            data-map-ui-control="true"
            class="absolute left-1/2 top-6 z-50 flex -translate-x-1/2 items-center gap-2 rounded-full border border-primary/30 bg-background/95 px-3 py-2 text-xs font-medium text-foreground shadow-xl backdrop-blur-sm"
          >
            <svg
              lucideIcon="mouse-pointer-2"
              class="h-4 w-4 text-primary"
            ></svg>
            <span>{{ placementInstruction }}</span>
            <button
              type="button"
              [class]="buttonClasses('ghost', 'sm', 'h-6 px-2 text-[10px]')"
              (click)="clearPlacement(); $event.stopPropagation()"
            >
              {{ language.t("common.cancel") }}
            </button>
          </div>

          <div
            class="absolute z-30 transition-all duration-700"
            [style.left.%]="journeyVehiclePosition.x + 4"
            [style.top.%]="journeyVehiclePosition.y + 5"
          >
            <div class="-translate-x-1/2 -translate-y-1/2 animate-float">
              <div [class]="journeyVehicleShellClass">
                <div [class]="journeyVehicleGlowClass"></div>
                <svg
                  [lucideIcon]="journeyVehicleIcon"
                  class="relative h-7 w-7 md:h-8 md:w-8"
                ></svg>
              </div>
              <span [class]="journeyVehicleLabelClass">
                {{ journeyVehicleLabel }}
              </span>
            </div>
          </div>

          <app-visual-island
            *ngFor="let island of islands; let index = index"
            [name]="island.name"
            [slug]="island.slug"
            [imageUrl]="island.image_url"
            [isLocked]="isIslandLocked(index)"
            [isCompleted]="completedIslandIds.has(island.id)"
            [isCurrent]="island.id === currentIslandId"
            [position]="islandPosition(index)"
            [levelNumber]="index + 1"
            [delay]="index"
            [dragTitle]="language.t('map.dragIsland')"
            [isBeingMoved]="draggingIslandId === island.id"
            (islandMouseDown)="handleIslandDragStart($event, island, index)"
            (islandClick)="handleIslandClick(island, index)"
          />
        </div>
      </div>

      <div
        class="fixed bottom-6 left-1/2 z-40 hidden -translate-x-1/2 lg:block"
      >
        <div
          [class]="
            cardClasses(
              'px-6 py-3 bg-card/90 backdrop-blur-sm shadow-xl border-2 border-secondary/30'
            )
          "
        >
          <div class="flex items-center gap-3">
            <svg
              lucideIcon="star"
              class="h-5 w-5 text-secondary fill-secondary"
            ></svg>
            <div>
              <h2 class="text-sm font-bold text-foreground">
                {{ language.t("map.mapTitle") }}
              </h2>
              <p class="text-xs text-muted-foreground">
                {{ language.t("map.mapSubtitle", { count: islands.length }) }}
              </p>
            </div>
            <svg
              lucideIcon="star"
              class="h-5 w-5 text-secondary fill-secondary"
            ></svg>
          </div>
        </div>
      </div>
    </div>
  `,
})
export class IslandMapComponent implements OnInit, OnDestroy {
  private readonly auth = inject(AuthService);
  readonly language = inject(LanguageService);
  readonly router = inject(Router);
  private readonly toast = inject(ToastService);
  private readonly changeDetector = inject(ChangeDetectorRef);

  @ViewChild("mapStage") mapStage?: ElementRef<HTMLDivElement>;

  islands: Island[] = [];
  progress: IslandProgress[] = [];
  stations: StationSummary[] = [];
  stationProgress: StationProgressSummary[] = [];
  placedItems: UserIslandItem[] = [];
  islandPositionsById: Record<string, MapPoint> = {};
  loading = true;
  shopOpen = false;
  shopPanelPosition = { x: 0, y: 0 };
  shopPanelHasCustomPosition = false;
  selectedShopItemKey: string | null = null;
  selectedPlacedItemId: string | null = null;
  savingIslandItem = false;
  isIslandItemsRemoteAvailable = true;
  zoom = 1;
  pan = { x: 0, y: 0 };
  isDragging = false;
  draggingIslandId: string | null = null;
  dragStart = { x: 0, y: 0 };
  user: User | null = null;
  profile: Profile | null = null;
  streak: UserStreak | null = null;
  dailyStreakBonusPoints = 0;
  isAdmin: boolean | null = null;
  isCrewOwner = false;
  crewId: string | null = null;
  pendingCrewInvitationCount = 0;
  crewGateBlockedIslandIds = new Set<string>();
  private animalMotionStates = new Map<string, AnimalMotionState>();
  private allIslandOrderById = new Map<string, number>();
  private animalMotionTimer?: number;
  private islandDragState: IslandDragState | null = null;
  private shopPanelDragState: ShopPanelDragState | null = null;
  private suppressNextIslandClickId: string | null = null;
  private lastAnimalMotionAt = performance.now();
  private fetchRequestId = 0;
  readonly minZoom = 0.5;
  readonly maxZoom = 2;
  readonly zoomStep = 0.25;
  readonly shopItems = ISLAND_SHOP_ITEMS;
  readonly personalIslandUpgrades = PERSONAL_ISLAND_UPGRADES;
  private subscriptions: Subscription[] = [];

  ngOnInit() {
    logMapStep("ngOnInit");
    this.lastAnimalMotionAt = performance.now();
    this.animalMotionTimer = window.setInterval(() => {
      this.advanceAnimalMotion();
    }, 80);

    this.subscriptions.push(
      this.auth.state$.subscribe((state) => {
        logMapStep("authState", {
          loading: state.loading,
          hasUser: Boolean(state.user),
          userId: state.user?.id ?? null,
          hasProfile: Boolean(state.profile),
          isAdmin: state.isAdmin,
          isCrewOwner: state.isCrewOwner,
          crewId: state.crewId,
        });
        this.user = state.user;
        this.profile = state.profile;
        this.streak = state.streak;
        this.isAdmin = state.isAdmin;
        this.isCrewOwner = state.isCrewOwner;
        this.crewId = state.crewId;
        this.changeDetector.detectChanges();
        void this.fetchData();
      }),
      this.language.language$.subscribe(() => {
        logMapStep("languageChanged", { language: this.language.language });
        this.changeDetector.detectChanges();
        void this.fetchData();
      }),
    );
  }

  ngOnDestroy() {
    logMapStep("ngOnDestroy");
    if (this.animalMotionTimer) {
      window.clearInterval(this.animalMotionTimer);
    }
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  get completedIslandIds() {
    return completedIslandIdsFromProgress(this.progress);
  }

  get totalScore() {
    return calculateJourneyScore(
      this.islands,
      this.stations,
      this.stationProgress,
      this.progress,
      this.dailyStreakBonusPoints,
    );
  }

  get nextDailyStreakBonus() {
    if (isDailyStreakWeekend()) return 0;
    return dailyStreakBonusForStreak((this.streak?.current_streak ?? 0) + 1);
  }

  get unlockedUpgrades() {
    return PERSONAL_ISLAND_UPGRADES.filter(
      (upgrade) => upgrade.unlockCount <= this.completedIslandIds.size,
    );
  }

  get nextUpgrade() {
    return PERSONAL_ISLAND_UPGRADES.find(
      (upgrade) => upgrade.unlockCount > this.completedIslandIds.size,
    );
  }

  get visibleAnimals() {
    return ISLAND_ANIMALS;
  }

  get progressPercentage() {
    return this.islands.length > 0
      ? (this.completedIslandIds.size / this.islands.length) * 100
      : 0;
  }

  get playerLevel() {
    if (this.islands.length === 0) return 1;
    return Math.min(this.completedIslandIds.size + 1, this.islands.length);
  }

  get currentIslandId() {
    return currentIslandIdFromProgress(this.islands, this.completedIslandIds);
  }

  get pirateShipPosition() {
    return this.journeyVehiclePosition;
  }

  get journeyVehiclePosition() {
    return this.routePointAtProgress(this.routeProgressIndex);
  }

  get journeyVehicle() {
    return this.vehicleForRouteIndex(this.routeProgressIndex);
  }

  get journeyVehicleIcon() {
    return this.journeyVehicle === "lorry" ? "truck" : "ship";
  }

  get journeyVehicleLabel() {
    return this.language.t(
      this.journeyVehicle === "lorry" ? "map.yourLorry" : "map.yourBoat",
    );
  }

  get journeyVehicleShellClass() {
    return cn(
      "relative grid h-12 w-12 place-items-center rounded-full border-2 border-white/85 text-white shadow-xl md:h-14 md:w-14",
      this.journeyVehicle === "lorry" ? "bg-amber-500/95" : "bg-sky-500/90",
    );
  }

  get journeyVehicleGlowClass() {
    return cn(
      "absolute inset-0 rounded-full blur-xl",
      this.journeyVehicle === "lorry" ? "bg-amber-300/35" : "bg-primary/30",
    );
  }

  get journeyVehicleLabelClass() {
    return cn(
      "absolute left-1/2 top-full mt-1 -translate-x-1/2 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold shadow-lg",
      this.journeyVehicle === "lorry"
        ? "bg-amber-500 text-white"
        : "bg-primary text-primary-foreground",
    );
  }

  get islandRoutePath() {
    return buildRoutePath(
      this.islands.map((_, index) => this.islandPosition(index)),
    );
  }

  get selectedShopItem() {
    return this.selectedShopItemKey
      ? getShopItemByKey(this.selectedShopItemKey)
      : null;
  }

  get selectedPlacedItem() {
    return this.selectedPlacedItemId
      ? this.placedItems.find(
          (item) => item.id === this.selectedPlacedItemId,
        ) || null
      : null;
  }

  get selectedPlacedCatalogItem() {
    return this.selectedPlacedItem
      ? getShopItemByKey(this.selectedPlacedItem.item_key)
      : null;
  }

  get placementMode() {
    return Boolean(this.selectedShopItem || this.selectedPlacedItem);
  }

  get routeProgress() {
    return this.islands.length > 1
      ? clampRatio(
          this.routeProgressIndex / Math.max(this.islands.length - 1, 1),
        )
      : 0;
  }

  private get routeProgressIndex() {
    if (this.islands.length === 0) return 0;

    const lastIslandIndex = this.islands.length - 1;
    const currentIslandIndex = this.islands.findIndex(
      (island) => !this.completedIslandIds.has(island.id),
    );

    if (currentIslandIndex < 0) return lastIslandIndex;

    const island = this.islands[currentIslandIndex];
    return Math.min(
      lastIslandIndex,
      currentIslandIndex + this.islandStationProgressRatio(island.id),
    );
  }

  private vehicleForRouteIndex(routeIndex: number): JourneyVehicle {
    if (this.islands.length <= 1) return "boat";

    const segmentIndex = Math.min(
      Math.max(Math.floor(routeIndex), 0),
      this.islands.length - 2,
    );
    const source = this.islands[segmentIndex];
    const destination = this.islands[segmentIndex + 1] || source;
    if (!source || !destination) return "boat";

    return isBoatRouteSegment(source.slug, destination.slug) ? "boat" : "lorry";
  }

  get placementInstruction() {
    if (this.selectedShopItem) {
      return this.language.t("map.placeInstruction", {
        label: this.shopItemLabel(this.selectedShopItem),
      });
    }

    return this.language.t("map.moveInstruction", {
      label: this.selectedPlacedCatalogItem
        ? this.shopItemLabel(this.selectedPlacedCatalogItem)
        : this.language.t("map.itemFallback"),
    });
  }

  get stageTransform() {
    return `scale(${this.zoom}) translate(${this.pan.x / this.zoom}px, ${this.pan.y / this.zoom}px)`;
  }

  private isFetchRequestCurrent(
    requestId: number,
    userId: string | null,
    crewId: string | null,
    language: string,
  ) {
    return (
      requestId === this.fetchRequestId &&
      (this.user?.id ?? null) === userId &&
      (this.crewId ?? null) === crewId &&
      this.language.language === language
    );
  }

  async fetchData() {
    const startedAt = performance.now();
    const requestId = ++this.fetchRequestId;
    const requestedUserId = this.user?.id ?? null;
    const requestedCrewId = this.crewId ?? null;
    const requestedLanguage = this.language.language;
    logMapStep("fetchData:start", {
      requestId,
      userId: requestedUserId,
      crewId: requestedCrewId,
      language: requestedLanguage,
    });

    try {
      logMapStep("fetchData:islands:start");
      const { data: islandsData, error: islandsError } = await backend
        .from("islands")
        .select("*")
        .order("order_index", { ascending: true });
      logMapStep("fetchData:islands:finish", {
        count: islandsData?.length ?? 0,
        hasError: Boolean(islandsError),
      });
      if (islandsError) throw islandsError;
      if (
        !this.isFetchRequestCurrent(
          requestId,
          requestedUserId,
          requestedCrewId,
          requestedLanguage,
        )
      )
        return;
      const rawIslands = ((islandsData || []) as Island[]).map((island) => ({
        ...island,
        name: resolveLocalizedText(
          island.name,
          island.name_translations,
          requestedLanguage,
        ),
        description: resolveLocalizedText(
          island.description,
          island.description_translations,
          requestedLanguage,
        ),
      }));
      this.allIslandOrderById = new Map(
        rawIslands.map((island) => [island.id, Number(island.order_index)]),
      );

      const crewIslands = requestedCrewId
        ? rawIslands.filter((island) => island.crew_id === requestedCrewId)
        : [];
      this.islands =
        crewIslands.length > 0
          ? crewIslands
          : rawIslands.filter((island) => !island.crew_id);
      logMapStep("fetchData:islands:selected", {
        rawCount: rawIslands.length,
        crewIslandCount: crewIslands.length,
        selectedCount: this.islands.length,
      });
      this.syncIslandPositions();

      const selectedIslandIds = new Set(
        this.islands.map((island) => island.id),
      );
      if (requestedUserId) {
        await syncCurrentUserGameProgress();
        if (
          !this.isFetchRequestCurrent(
            requestId,
            requestedUserId,
            requestedCrewId,
            requestedLanguage,
          )
        )
          return;

        logMapStep("fetchData:islandProgress:start", {
          userId: requestedUserId,
        });
        const { data: progressData, error: progressError } = await backend
          .from("island_progress")
          .select("user_id, island_id, score, completed_at")
          .eq("user_id", requestedUserId);
        logMapStep("fetchData:islandProgress:finish", {
          count: progressData?.length ?? 0,
          hasError: Boolean(progressError),
        });
        if (progressError) throw progressError;
        if (
          !this.isFetchRequestCurrent(
            requestId,
            requestedUserId,
            requestedCrewId,
            requestedLanguage,
          )
        )
          return;
        this.progress = filterIslandProgressForJourney(
          (progressData as IslandProgress[]) || [],
          requestedUserId,
          selectedIslandIds,
        );
      } else {
        logMapStep("fetchData:islandProgress:skip", { reason: "no-user" });
        this.progress = [];
      }

      logMapStep("fetchData:stations:start");
      const { data: stationsData, error: stationsError } = await backend
        .from("stations")
        .select("id, island_id, slug, source_station_id");
      logMapStep("fetchData:stations:finish", {
        count: stationsData?.length ?? 0,
        hasError: Boolean(stationsError),
      });
      if (stationsError) throw stationsError;
      if (
        !this.isFetchRequestCurrent(
          requestId,
          requestedUserId,
          requestedCrewId,
          requestedLanguage,
        )
      )
        return;
      const islandSlugsById = new Map(
        rawIslands.map((island) => [island.id, island.slug]),
      );
      this.stations = ((stationsData as StationSummary[]) || []).filter(
        (station) => {
          const islandSlug = islandSlugsById.get(station.island_id);
          return (
            !islandSlug || isQuestionBankStationForIsland(islandSlug, station)
          );
        },
      );
      const selectedStationIds = new Set(
        this.stations
          .filter((station) => selectedIslandIds.has(station.island_id))
          .map((station) => station.id),
      );

      if (requestedUserId) {
        logMapStep("fetchData:stationProgress:start", {
          userId: requestedUserId,
        });
        const { data: stationProgressData, error: stationProgressError } =
          await backend
            .from("station_progress")
            .select(
              "user_id, station_id, current_stage, stage_points, station_points, completed_at",
            )
            .eq("user_id", requestedUserId);

        logMapStep("fetchData:stationProgress:finish", {
          count: stationProgressData?.length ?? 0,
          hasError: Boolean(stationProgressError),
        });
        if (stationProgressError) throw stationProgressError;
        if (
          !this.isFetchRequestCurrent(
            requestId,
            requestedUserId,
            requestedCrewId,
            requestedLanguage,
          )
        )
          return;
        this.stationProgress = filterStationProgressForJourney(
          (stationProgressData as StationProgressSummary[]) || [],
          requestedUserId,
          selectedStationIds,
        );
        this.mergeStoredStationProgress(requestedUserId);
        await this.repairCompletedIslandProgressFromStations(requestedUserId);
        if (
          !this.isFetchRequestCurrent(
            requestId,
            requestedUserId,
            requestedCrewId,
            requestedLanguage,
          )
        )
          return;

        logMapStep("fetchData:dailyStreakBonus:start", {
          userId: requestedUserId,
        });
        const dailyBonusResult = await loadDailyStreakBonusTotals([
          requestedUserId,
        ]);
        if (
          !this.isFetchRequestCurrent(
            requestId,
            requestedUserId,
            requestedCrewId,
            requestedLanguage,
          )
        )
          return;
        this.dailyStreakBonusPoints =
          dailyBonusResult.totals.get(requestedUserId) ?? 0;
        logMapStep("fetchData:dailyStreakBonus:finish", {
          points: this.dailyStreakBonusPoints,
          storageAvailable: dailyBonusResult.storageAvailable,
        });

        logMapStep("fetchData:pendingCrewInvites:start");
        await this.loadPendingCrewInvitationCount();
        if (
          !this.isFetchRequestCurrent(
            requestId,
            requestedUserId,
            requestedCrewId,
            requestedLanguage,
          )
        )
          return;
        logMapStep("fetchData:pendingCrewInvites:finish", {
          count: this.pendingCrewInvitationCount,
        });

        logMapStep("fetchData:placedItems:start");
        const { data: placedItemsData, error: placedItemsError } = await backend
          .from("user_island_items")
          .select("id, user_id, item_key, x, y, created_at, updated_at")
          .eq("user_id", requestedUserId)
          .order("created_at", { ascending: true });

        logMapStep("fetchData:placedItems:finish", {
          count: placedItemsData?.length ?? 0,
          hasError: Boolean(placedItemsError),
        });
        if (
          !this.isFetchRequestCurrent(
            requestId,
            requestedUserId,
            requestedCrewId,
            requestedLanguage,
          )
        )
          return;

        if (placedItemsError) {
          if (isIslandItemsPersistenceError(placedItemsError)) {
            console.warn(
              "Island item persistence is not available yet:",
              placedItemsError,
            );
            this.isIslandItemsRemoteAvailable = false;
            this.placedItems = readLocalIslandItems(requestedUserId);
          } else {
            throw placedItemsError;
          }
        } else {
          this.isIslandItemsRemoteAvailable = true;
          this.placedItems = (
            (placedItemsData || []) as UserIslandItem[]
          ).filter((item) => item.user_id === requestedUserId);
        }

        logMapStep("fetchData:crewGate:start");
        await this.loadCrewGateData();
        if (
          !this.isFetchRequestCurrent(
            requestId,
            requestedUserId,
            requestedCrewId,
            requestedLanguage,
          )
        )
          return;
        logMapStep("fetchData:crewGate:finish", {
          blockedCount: this.crewGateBlockedIslandIds.size,
        });
      } else {
        logMapStep("fetchData:noUser");
        this.stationProgress = [];
        this.dailyStreakBonusPoints = 0;
        this.pendingCrewInvitationCount = 0;
        this.isIslandItemsRemoteAvailable = true;
        this.placedItems = [];
        this.crewGateBlockedIslandIds = new Set<string>();
      }
    } catch (error) {
      if (
        !this.isFetchRequestCurrent(
          requestId,
          requestedUserId,
          requestedCrewId,
          requestedLanguage,
        )
      )
        return;
      console.error("Error fetching data:", error);
      this.toast.error(this.language.t("map.loadError"));
    } finally {
      const isCurrentRequest = this.isFetchRequestCurrent(
        requestId,
        requestedUserId,
        requestedCrewId,
        requestedLanguage,
      );
      logMapStep("fetchData:finish", {
        requestId,
        durationMs: Math.round(performance.now() - startedAt),
        loading: isCurrentRequest ? false : this.loading,
        stale: !isCurrentRequest,
      });
      if (isCurrentRequest) {
        this.loading = false;
        this.changeDetector.detectChanges();
      }
    }
  }

  private async repairCompletedIslandProgressFromStations(userId: string) {
    let repaired = false;

    for (const island of this.islands) {
      const remoteScore = this.remoteCompletedStationScoreForIsland(island.id);
      const storedScore = this.storedCompletedStationScoreForIsland(
        userId,
        island,
      );
      const completedScore = Math.max(remoteScore ?? -1, storedScore ?? -1);
      if (completedScore < 0) continue;

      const existingProgress = this.progress.find(
        (progress) =>
          progress.user_id === userId && progress.island_id === island.id,
      );
      if (
        existingProgress?.completed_at &&
        safeCompletedPoints(existingProgress.score) >= completedScore
      ) {
        continue;
      }

      try {
        const didRepair = await this.upsertCompletedIslandProgress(
          userId,
          island,
          completedScore,
          existingProgress,
        );
        repaired = repaired || didRepair;
      } catch (error) {
        console.warn("Could not repair completed island progress:", {
          islandId: island.id,
          error,
        });
      }
    }

    if (repaired) {
      await syncCurrentUserGameProgress();
    }
  }

  private mergeStoredStationProgress(userId: string) {
    for (const island of this.islands) {
      const routeStationIds = getQuestionBankStationIdsForIsland(island.slug);
      if (!routeStationIds?.length) continue;

      for (const routeStationId of routeStationIds) {
        const snapshot = loadStoredPlaytestProgress(
          window.localStorage,
          getPlaytestProgressKey({
            userId,
            islandSlug: island.slug,
            stationId: routeStationId,
          }),
        );
        if (!snapshot) continue;

        const completedScore = storedStationCompletionScore(snapshot);
        const localScore = safeCompletedPoints(
          snapshot.totalScore ?? snapshot.session?.stationPoints ?? 0,
        );
        if (localScore <= 0 && completedScore === null) continue;

        const routeKey = normalizeStationProgressKey(routeStationId);
        const matchingStations = this.stations.filter(
          (station) =>
            station.island_id === island.id &&
            [
              station.id,
              station.slug,
              station.source_station_id,
            ].some(
              (value) => normalizeStationProgressKey(value) === routeKey,
            ),
        );
        const targetStation = matchingStations[0] ?? {
          id: routeStationId,
          island_id: island.id,
          slug: routeStationId.replace(/_/g, "-"),
          source_station_id: routeStationId,
        };
        const matchingStationIds = new Set([
          routeStationId,
          ...matchingStations.map((station) => station.id),
        ]);
        const bestRemoteScore = Math.max(
          -1,
          ...this.stationProgress
            .filter((progress) => matchingStationIds.has(progress.station_id))
            .map((progress) => safeCompletedPoints(progress.station_points)),
        );

        if (bestRemoteScore >= localScore) continue;

        if (!this.stations.some((station) => station.id === targetStation.id)) {
          this.stations = [...this.stations, targetStation];
        }

        this.stationProgress = [
          ...this.stationProgress.filter(
            (progress) => !matchingStationIds.has(progress.station_id),
          ),
          {
            user_id: userId,
            station_id: targetStation.id,
            current_stage:
              snapshot.currentStage ?? snapshot.session?.currentStage ?? 1,
            stage_points:
              snapshot.stageScore ?? snapshot.session?.stagePoints ?? 0,
            station_points: localScore,
            completed_at:
              completedScore !== null ? new Date().toISOString() : null,
          },
        ];
      }
    }
  }

  private remoteCompletedStationScoreForIsland(islandId: string) {
    const stationIds = this.stations
      .filter((station) => station.island_id === islandId)
      .map((station) => station.id);
    if (stationIds.length === 0) return null;

    const progressByStationId = new Map(
      this.stationProgress.map((progress) => [progress.station_id, progress]),
    );
    const stationRows = stationIds.map((stationId) =>
      progressByStationId.get(stationId),
    );
    if (!stationRows.every((progress) => Boolean(progress?.completed_at))) {
      return null;
    }

    return stationRows.reduce(
      (sum, progress) => sum + safeCompletedPoints(progress?.station_points),
      0,
    );
  }

  private storedCompletedStationScoreForIsland(userId: string, island: Island) {
    const scores = this.storedStationIdGroupsForIsland(island)
      .map((stationIds) => {
        const stationScores = stationIds.map((stationId) =>
          storedStationCompletionScore(
            loadStoredPlaytestProgress(
              window.localStorage,
              getPlaytestProgressKey({
                userId,
                islandSlug: island.slug,
                stationId,
              }),
            ),
          ),
        );

        return stationScores.every((score) => score !== null)
          ? stationScores.reduce((sum, score) => sum + (score ?? 0), 0)
          : null;
      })
      .filter((score): score is number => score !== null);

    return scores.length > 0 ? Math.max(...scores) : null;
  }

  private storedStationIdGroupsForIsland(island: Island) {
    const groups: string[][] = [];
    const routeStationIds = getQuestionBankStationIdsForIsland(island.slug);
    if (routeStationIds?.length) groups.push(routeStationIds);

    const remoteStationIds = this.stations
      .filter((station) => station.island_id === island.id)
      .map((station) => station.id);
    if (remoteStationIds.length > 0) groups.push(remoteStationIds);

    const seenGroups = new Set<string>();
    return groups
      .map((group) => Array.from(new Set(group.filter(Boolean))))
      .filter((group) => {
        if (group.length === 0) return false;
        const key = group.join("\u0000");
        if (seenGroups.has(key)) return false;
        seenGroups.add(key);
        return true;
      });
  }

  private async upsertCompletedIslandProgress(
    userId: string,
    island: Island,
    score: number,
    existingProgress?: IslandProgress,
  ) {
    const completedAt =
      existingProgress?.completed_at ?? new Date().toISOString();
    const finalScore = Math.max(
      safeCompletedPoints(score),
      safeCompletedPoints(existingProgress?.score),
    );

    if (existingProgress?.island_id === island.id) {
      const { error: updateError } = await backend
        .from("island_progress")
        .update({
          score: finalScore,
          completed_at: completedAt,
        })
        .eq("user_id", userId)
        .eq("island_id", island.id);
      if (updateError) throw updateError;
    } else {
      const { error: insertError } = await backend
        .from("island_progress")
        .insert({
          user_id: userId,
          island_id: island.id,
          score: finalScore,
          completed_at: completedAt,
        });

      if (insertError) {
        const { error: updateError } = await backend
          .from("island_progress")
          .update({
            score: finalScore,
            completed_at: completedAt,
          })
          .eq("user_id", userId)
          .eq("island_id", island.id);
        if (updateError) throw updateError;
      }
    }

    const savedProgress = {
      user_id: userId,
      island_id: island.id,
      score: finalScore,
      completed_at: completedAt,
    };
    this.progress = [
      ...this.progress.filter((progress) => progress.island_id !== island.id),
      savedProgress,
    ];

    return true;
  }

  handleZoomIn() {
    this.zoom = Math.min(this.zoom + this.zoomStep, this.maxZoom);
  }

  handleZoomOut() {
    this.zoom = Math.max(this.zoom - this.zoomStep, this.minZoom);
  }

  handleReset() {
    this.zoom = 1;
    this.pan = { x: 0, y: 0 };
  }

  resetIslandLayout() {
    if (this.user?.id) clearLocalIslandPositions(this.user.id);
    this.islandPositionsById = this.defaultIslandPositions();
    this.toast.success(this.language.t("map.resetIslandLayoutSuccess"));
  }

  handleWheel(event: WheelEvent) {
    if (event.ctrlKey || Math.abs(event.deltaY) < 50) {
      event.preventDefault();
      const delta = -event.deltaY * 0.01;
      this.zoom = Math.min(
        Math.max(this.zoom + delta, this.minZoom),
        this.maxZoom,
      );
    }
  }

  handleMouseDown(event: MouseEvent) {
    if (
      this.draggingIslandId ||
      this.selectedShopItemKey ||
      this.selectedPlacedItemId ||
      event.button !== 0
    )
      return;
    const target = event.target as HTMLElement;
    if (
      target.closest("[data-island]") ||
      target.closest("[data-map-ui-control]") ||
      target.closest("[data-placed-item]")
    )
      return;
    this.isDragging = true;
    this.dragStart = {
      x: event.clientX - this.pan.x,
      y: event.clientY - this.pan.y,
    };
  }

  handleMouseMove(event: MouseEvent) {
    if (this.draggingIslandId) {
      this.handleIslandDragMove(event);
      return;
    }

    if (!this.isDragging) return;
    this.pan = {
      x: event.clientX - this.dragStart.x,
      y: event.clientY - this.dragStart.y,
    };
  }

  handleMouseUp() {
    this.handleIslandDragEnd();
    this.isDragging = false;
  }

  handleMouseLeave() {
    this.handleIslandDragEnd();
    this.isDragging = false;
  }

  async handleMapPlacementClick(event: MouseEvent) {
    if (!this.selectedShopItem && !this.selectedPlacedItem) return;

    const target = event.target as HTMLElement;
    if (
      target.closest("[data-island]") ||
      target.closest("[data-map-ui-control]")
    )
      return;

    const point = this.stagePointFromEvent(event);
    if (!point) return;
    const { x, y } = point;

    if (!isPointInsideIslandLand(x, y)) {
      this.toast.info(this.language.t("map.chooseLand"));
      return;
    }

    if (this.selectedShopItem) {
      await this.addPlacedItem(this.selectedShopItem, x, y);
      return;
    }

    if (this.selectedPlacedItem) {
      await this.movePlacedItem(this.selectedPlacedItem, x, y);
    }
  }

  handleIslandDragStart(event: MouseEvent, island: Island, index: number) {
    if (this.placementMode || event.button !== 0) return;

    const point = this.stagePointFromEvent(event);
    if (!point) return;

    event.preventDefault();
    event.stopPropagation();

    const position = this.islandPosition(index);
    this.draggingIslandId = island.id;
    this.islandDragState = {
      islandId: island.id,
      offsetX: point.x - position.x,
      offsetY: point.y - position.y,
      startClientX: event.clientX,
      startClientY: event.clientY,
      hasMoved: false,
    };
  }

  private handleIslandDragMove(event: MouseEvent) {
    if (!this.islandDragState) return;

    const distance = Math.hypot(
      event.clientX - this.islandDragState.startClientX,
      event.clientY - this.islandDragState.startClientY,
    );
    if (distance < ISLAND_DRAG_THRESHOLD_PX && !this.islandDragState.hasMoved)
      return;

    const point = this.stagePointFromEvent(event);
    if (!point) return;

    this.islandDragState.hasMoved = true;
    this.islandPositionsById = {
      ...this.islandPositionsById,
      [this.islandDragState.islandId]: {
        x: clampIslandCoordinate(point.x - this.islandDragState.offsetX),
        y: clampIslandCoordinate(point.y - this.islandDragState.offsetY),
      },
    };
  }

  private handleIslandDragEnd() {
    if (!this.islandDragState) return;

    if (this.islandDragState.hasMoved) {
      this.suppressNextIslandClickId = this.islandDragState.islandId;
      this.saveIslandPositions();
    }

    this.draggingIslandId = null;
    this.islandDragState = null;
  }

  handleIslandClick(island: Island, index: number) {
    if (this.suppressNextIslandClickId === island.id) {
      this.suppressNextIslandClickId = null;
      return;
    }

    if (this.placementMode) return;
    if (this.isIslandLocked(index)) {
      this.toast.info(
        this.isCrewGateBlocked(index)
          ? this.language.t("map.crewAdvanceBlocked")
          : this.language.t("map.completePrevious"),
      );
      return;
    }
    void this.router.navigateByUrl(`/island/${island.slug}`);
  }

  async handleLogout() {
    await this.auth.signOut();
    await this.router.navigateByUrl("/auth");
  }

  async addShopItemToIsland(item: IslandShopItem) {
    if (this.isCompactMapViewport()) {
      if (!this.shopItemUnlocked(item)) {
        this.toast.info(
          this.language.t("map.unlockToast", {
            level: item.unlockCount,
            points: item.unlockScore,
          }),
        );
        return;
      }

      const position = this.nextShopItemPosition(item);
      await this.addPlacedItem(item, position.x, position.y);
      return;
    }

    this.selectShopItem(item);
  }

  get shopPanelStyle(): Record<string, string> {
    if (this.isCompactMapViewport()) {
      return {
        left: "0.75rem",
        right: "0.75rem",
        bottom: "0.75rem",
        top: "auto",
      };
    }

    if (!this.shopPanelHasCustomPosition) {
      return {
        right: "1rem",
        top: "11rem",
      };
    }

    return {
      left: `${this.shopPanelPosition.x}px`,
      top: `${this.shopPanelPosition.y}px`,
    };
  }

  handleShopPanelDragStart(event: PointerEvent) {
    if (event.pointerType === "mouse" && event.button !== 0) return;

    const panel = (event.currentTarget as HTMLElement).closest(
      "[data-shop-panel]",
    ) as HTMLElement | null;
    if (!panel) return;

    const rect = panel.getBoundingClientRect();
    const position = this.constrainShopPanelPosition(
      rect.left,
      rect.top,
      rect.width,
      rect.height,
    );

    this.shopPanelHasCustomPosition = true;
    this.shopPanelPosition = position;
    this.shopPanelDragState = {
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      width: rect.width,
      height: rect.height,
    };

    event.preventDefault();
    event.stopPropagation();
  }

  handleShopPanelDragMove(event: PointerEvent) {
    if (!this.shopPanelDragState) return;

    const position = this.constrainShopPanelPosition(
      event.clientX - this.shopPanelDragState.offsetX,
      event.clientY - this.shopPanelDragState.offsetY,
      this.shopPanelDragState.width,
      this.shopPanelDragState.height,
    );

    this.shopPanelPosition = position;
    event.preventDefault();
    event.stopPropagation();
  }

  handleShopPanelDragEnd() {
    this.shopPanelDragState = null;
  }

  private constrainShopPanelPosition(
    left: number,
    top: number,
    width: number,
    height: number,
  ) {
    const maxLeft = Math.max(
      SHOP_PANEL_MARGIN_PX,
      window.innerWidth - width - SHOP_PANEL_MARGIN_PX,
    );
    const maxTop = Math.max(
      SHOP_PANEL_MARGIN_PX,
      window.innerHeight - height - SHOP_PANEL_MARGIN_PX,
    );

    return {
      x: Math.min(Math.max(left, SHOP_PANEL_MARGIN_PX), maxLeft),
      y: Math.min(Math.max(top, SHOP_PANEL_MARGIN_PX), maxTop),
    };
  }

  nextShopItemPosition(item: IslandShopItem) {
    const spreadOffsets = [
      { x: 0, y: 0 },
      { x: 4, y: -2 },
      { x: -4, y: 2 },
      { x: 5, y: 3 },
      { x: -5, y: -3 },
      { x: 0, y: 5 },
      { x: 6, y: 0 },
      { x: -6, y: 0 },
    ];
    const sameItemCount = this.placedItems.filter(
      (placedItem) => placedItem.item_key === item.key,
    ).length;
    const startIndex = sameItemCount % spreadOffsets.length;

    for (let index = 0; index < spreadOffsets.length; index += 1) {
      const offset = spreadOffsets[(startIndex + index) % spreadOffsets.length];
      const x = clampMapCoordinate(item.defaultX + offset.x);
      const y = clampMapCoordinate(item.defaultY + offset.y);
      const overlapsExistingItem = this.placedItems.some(
        (placedItem) =>
          Math.abs(placedItem.x - x) < 2 && Math.abs(placedItem.y - y) < 2,
      );

      if (!overlapsExistingItem && isPointInsideIslandLand(x, y)) {
        return { x, y };
      }
    }

    return { x: item.defaultX, y: item.defaultY };
  }

  selectShopItem(item: IslandShopItem) {
    if (!this.shopItemUnlocked(item)) {
      this.toast.info(
        this.language.t("map.unlockToast", {
          level: item.unlockCount,
          points: item.unlockScore,
        }),
      );
      return;
    }
    this.selectedShopItemKey = item.key;
    this.selectedPlacedItemId = null;
    this.toast.info(this.placementInstruction);
  }

  selectPlacedItem(item: UserIslandItem) {
    this.selectedPlacedItemId = item.id;
    this.selectedShopItemKey = null;
    this.toast.info(this.placementInstruction);
  }

  clearPlacement() {
    this.selectedShopItemKey = null;
    this.selectedPlacedItemId = null;
  }

  async addPlacedItem(catalogItem: IslandShopItem, x: number, y: number) {
    if (!this.user) {
      this.toast.error(this.language.t("map.signInCustomize"));
      return;
    }

    if (!this.isIslandItemsRemoteAvailable) {
      this.addLocalPlacedItem(catalogItem, x, y);
      return;
    }

    this.savingIslandItem = true;
    this.changeDetector.detectChanges();
    try {
      const { data, error } = await backend
        .from("user_island_items")
        .insert({ user_id: this.user.id, item_key: catalogItem.key, x, y })
        .select("id, user_id, item_key, x, y, created_at, updated_at")
        .single();

      if (error) throw error;
      this.placedItems = [...this.placedItems, data];
      this.selectedShopItemKey = null;
      this.toast.success(
        this.language.t("map.addSuccess", {
          label: this.shopItemLabel(catalogItem),
        }),
      );
    } catch (error) {
      console.error("Error adding island item:", error);
      if (isIslandItemsPersistenceError(error)) {
        this.isIslandItemsRemoteAvailable = false;
        this.addLocalPlacedItem(catalogItem, x, y);
        this.toast.info(this.language.t("map.addLocalInfo"));
      } else {
        this.toast.error(this.language.t("map.addError"));
      }
    } finally {
      this.savingIslandItem = false;
      this.changeDetector.detectChanges();
    }
  }

  async movePlacedItem(item: UserIslandItem, x: number, y: number) {
    if (!this.isIslandItemsRemoteAvailable || isLocalIslandItem(item)) {
      const updatedItem = {
        ...item,
        x,
        y,
        updated_at: new Date().toISOString(),
      };
      this.placedItems = this.placedItems.map((currentItem) =>
        currentItem.id === item.id ? updatedItem : currentItem,
      );
      this.savePlacedItemsLocally(this.placedItems);
      this.selectedPlacedItemId = null;
      this.toast.success(this.language.t("map.moveSuccess"));
      return;
    }

    this.savingIslandItem = true;
    this.changeDetector.detectChanges();
    try {
      const { data, error } = await backend
        .from("user_island_items")
        .update({ x, y })
        .eq("id", item.id)
        .select("id, user_id, item_key, x, y, created_at, updated_at")
        .single();

      if (error) throw error;
      this.placedItems = this.placedItems.map((currentItem) =>
        currentItem.id === data.id ? data : currentItem,
      );
      this.selectedPlacedItemId = null;
      this.toast.success(this.language.t("map.moveSuccess"));
    } catch (error) {
      console.error("Error moving island item:", error);
      if (isIslandItemsPersistenceError(error)) {
        this.isIslandItemsRemoteAvailable = false;
        const updatedItem = {
          ...item,
          x,
          y,
          updated_at: new Date().toISOString(),
        };
        this.placedItems = this.placedItems.map((currentItem) =>
          currentItem.id === item.id ? updatedItem : currentItem,
        );
        this.savePlacedItemsLocally(this.placedItems);
        this.selectedPlacedItemId = null;
        this.toast.info(this.language.t("map.moveLocalInfo"));
      } else {
        this.toast.error(this.language.t("map.moveError"));
      }
    } finally {
      this.savingIslandItem = false;
      this.changeDetector.detectChanges();
    }
  }

  async deletePlacedItem(item: UserIslandItem) {
    if (!this.isIslandItemsRemoteAvailable || isLocalIslandItem(item)) {
      this.placedItems = this.placedItems.filter(
        (currentItem) => currentItem.id !== item.id,
      );
      this.savePlacedItemsLocally(this.placedItems);
      if (this.selectedPlacedItemId === item.id)
        this.selectedPlacedItemId = null;
      this.toast.success(this.language.t("map.removeSuccess"));
      return;
    }

    this.savingIslandItem = true;
    this.changeDetector.detectChanges();
    try {
      const { error } = await backend
        .from("user_island_items")
        .delete()
        .eq("id", item.id);
      if (error) throw error;
      this.placedItems = this.placedItems.filter(
        (currentItem) => currentItem.id !== item.id,
      );
      if (this.selectedPlacedItemId === item.id)
        this.selectedPlacedItemId = null;
      this.toast.success(this.language.t("map.removeSuccess"));
    } catch (error) {
      console.error("Error deleting island item:", error);
      if (isIslandItemsPersistenceError(error)) {
        this.isIslandItemsRemoteAvailable = false;
        this.placedItems = this.placedItems.filter(
          (currentItem) => currentItem.id !== item.id,
        );
        this.savePlacedItemsLocally(this.placedItems);
        if (this.selectedPlacedItemId === item.id)
          this.selectedPlacedItemId = null;
        this.toast.info(this.language.t("map.removeLocalInfo"));
      } else {
        this.toast.error(this.language.t("map.removeError"));
      }
    } finally {
      this.savingIslandItem = false;
      this.changeDetector.detectChanges();
    }
  }

  addLocalPlacedItem(catalogItem: IslandShopItem, x: number, y: number) {
    if (!this.user?.id) return;
    const localItem = createLocalIslandItem(
      this.user.id,
      catalogItem.key,
      x,
      y,
    );
    this.placedItems = [...this.placedItems, localItem];
    saveLocalIslandItems(this.user.id, this.placedItems);
    this.selectedShopItemKey = null;
    this.toast.success(
      this.language.t("map.addSuccess", {
        label: this.shopItemLabel(catalogItem),
      }),
    );
  }

  savePlacedItemsLocally(nextItems: UserIslandItem[]) {
    if (this.user?.id) saveLocalIslandItems(this.user.id, nextItems);
  }

  islandPosition(index: number) {
    const island = this.islands[index];
    if (!island) return getIslandPosition(index, this.islands.length);
    return (
      this.islandPositionsById[island.id] ||
      preferredIslandPosition(island, index, this.islands.length)
    );
  }

  private routePointAtProgress(routeIndex: number) {
    return routePointAtIndex(
      this.islands.map((_, index) => this.islandPosition(index)),
      routeIndex,
    );
  }

  private islandStationProgressRatio(islandId: string) {
    if (this.completedIslandIds.has(islandId)) return 1;

    const stationIds = this.stations
      .filter((station) => station.island_id === islandId)
      .map((station) => station.id);
    if (stationIds.length === 0) return 0;

    const progressByStationId = new Map(
      this.stationProgress.map((progress) => [progress.station_id, progress]),
    );
    const totalStationProgress = stationIds.reduce(
      (sum, stationId) =>
        sum + this.stationProgressRatio(progressByStationId.get(stationId)),
      0,
    );

    return clampRatio(totalStationProgress / stationIds.length);
  }

  private stationProgressRatio(progress?: StationProgressSummary) {
    if (!progress) return 0;
    if (progress.completed_at) return 1;

    const currentStage = Math.min(
      Math.max(Math.trunc(Number(progress.current_stage ?? 1)), 1),
      STAGES_PER_STATION,
    );
    const completedStageCount = currentStage - 1;
    const currentStageProgress = clampRatio(
      Number(progress.stage_points ?? 0) / STAGE_TARGET_POINTS,
    );
    const stageBasedProgress =
      (completedStageCount + currentStageProgress) / STAGES_PER_STATION;
    const pointBasedProgress = clampRatio(
      Number(progress.station_points ?? 0) /
        (STAGES_PER_STATION * STAGE_TARGET_POINTS),
    );

    return clampRatio(Math.max(stageBasedProgress, pointBasedProgress * 0.95));
  }

  private stagePointFromEvent(event: MouseEvent): MapPoint | null {
    const rect = this.mapStage?.nativeElement.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return null;

    return {
      x: clampMapCoordinate(((event.clientX - rect.left) / rect.width) * 100),
      y: clampMapCoordinate(((event.clientY - rect.top) / rect.height) * 100),
    };
  }

  private defaultIslandPositions() {
    return this.islands.reduce<Record<string, MapPoint>>(
      (positions, island, index) => {
        positions[island.id] = preferredIslandPosition(
          island,
          index,
          this.islands.length,
        );
        return positions;
      },
      {},
    );
  }

  private syncIslandPositions() {
    const savedPositions = this.user?.id
      ? readLocalIslandPositions(this.user.id)
      : {};
    this.islandPositionsById = this.islands.reduce<Record<string, MapPoint>>(
      (positions, island, index) => {
        positions[island.id] =
          savedPositions[island.id] ||
          preferredIslandPosition(island, index, this.islands.length);
        return positions;
      },
      {},
    );
  }

  private saveIslandPositions() {
    if (this.user?.id)
      saveLocalIslandPositions(this.user.id, this.islandPositionsById);
  }

  isIslandLocked(index: number) {
    return (
      (index > 0 && !this.completedIslandIds.has(this.islands[index - 1].id)) ||
      this.isCrewGateBlocked(index)
    );
  }

  isCrewGateBlocked(index: number) {
    return Boolean(
      this.islands[index] &&
      this.crewGateBlockedIslandIds.has(this.islands[index].id),
    );
  }

  shopItemUnlocked(item: IslandShopItem) {
    return isShopItemUnlocked(item, this.playerLevel, this.totalScore);
  }

  shopItemLabel(item: IslandShopItem) {
    const key = `shopItem.${item.key}.label`;
    const translated = this.language.t(key);
    return translated === key ? item.label : translated;
  }

  shopItemDescription(item: IslandShopItem) {
    const key = `shopItem.${item.key}.description`;
    const translated = this.language.t(key);
    return translated === key ? item.description : translated;
  }

  placedItemLabel(item: UserIslandItem) {
    const catalogItem = getShopItemByKey(item.item_key);
    return catalogItem ? this.shopItemLabel(catalogItem) : item.item_key;
  }

  placedItemKind(item: UserIslandItem) {
    return getShopItemByKey(item.item_key)?.kind || "camp";
  }

  placedItemSize(item: UserIslandItem): UpgradeSize {
    return getShopItemByKey(item.item_key)?.size || "small";
  }

  personalUpgradeSizeClass(size: UpgradeSize = "medium") {
    const sizeClass: Record<UpgradeSize, string> = {
      tiny: "h-5 w-5 md:h-7 md:w-7",
      small: "h-7 w-7 md:h-9 md:w-9",
      medium: "h-9 w-9 md:h-12 md:w-12",
      large: "h-11 w-11 md:h-14 md:w-14",
    };
    return sizeClass[size];
  }

  playerItemSizeClass(size: UpgradeSize) {
    const sizeClass: Record<UpgradeSize, string> = {
      tiny: "h-6 w-6 md:h-8 md:w-8",
      small: "h-8 w-8 md:h-11 md:w-11",
      medium: "h-10 w-10 md:h-14 md:w-14",
      large: "h-12 w-12 md:h-16 md:w-16",
    };
    return sizeClass[size];
  }

  animalClass(animal: IslandAnimal) {
    const motionClass: Record<IslandAnimal["motion"], string> = {
      wander: "animate-fauna-wander",
      fly: "animate-fauna-fly",
      swim: "animate-fauna-swim",
      bob: "animate-fauna-bob",
    };

    return motionClass[animal.motion];
  }

  animalPosition(animal: IslandAnimal) {
    const state =
      this.animalMotionStates.get(animal.id) ||
      this.ensureAnimalMotionState(animal, this.lastAnimalMotionAt);
    const config = animalMotionConfig(animal);
    const position = {
      x: state.x,
      y: Math.min(state.y + state.floatOffset, config.maxY),
    };

    if (isAnimalPointAllowed(animal, position)) {
      return position;
    }

    return {
      x: state.x,
      y: state.y,
    };
  }

  private ensureAnimalMotionState(animal: IslandAnimal, now: number) {
    let state = this.animalMotionStates.get(animal.id);
    if (!state) {
      const config = animalMotionConfig(animal);
      const animalStartPoint = { x: animal.x, y: animal.y };
      const startPoint = isAnimalPointAllowed(animal, animalStartPoint)
        ? { x: animal.x, y: animal.y }
        : randomPointForAnimal(animal, config);
      state = {
        x: startPoint.x,
        y: startPoint.y,
        floatOffset: 0,
        targetX: startPoint.x,
        targetY: startPoint.y,
        nextTargetAt: 0,
      };
      this.pickAnimalTarget(animal, state, now);
      this.animalMotionStates.set(animal.id, state);
    }

    return state;
  }

  private pickAnimalTarget(
    animal: IslandAnimal,
    state: AnimalMotionState,
    now: number,
  ) {
    const config = animalMotionConfig(animal);
    const target = randomPointForAnimal(animal, config);
    state.targetX = target.x;
    state.targetY = target.y;
    state.nextTargetAt =
      now + randomBetween(config.minTargetMs, config.maxTargetMs);
  }

  private resetAnimalToAllowedPoint(
    animal: IslandAnimal,
    state: AnimalMotionState,
    config: AnimalMotionConfig,
    now: number,
  ) {
    const point = randomPointForAnimal(animal, config);
    state.x = point.x;
    state.y = point.y;
    state.targetX = point.x;
    state.targetY = point.y;
    state.floatOffset = 0;
    this.pickAnimalTarget(animal, state, now);
  }

  private advanceAnimalMotion() {
    const now = performance.now();
    const elapsedSeconds = Math.min(
      (now - this.lastAnimalMotionAt) / 1000,
      0.2,
    );
    this.lastAnimalMotionAt = now;

    this.visibleAnimals.forEach((animal) => {
      const config = animalMotionConfig(animal);
      const state = this.ensureAnimalMotionState(animal, now);
      if (!isAnimalPointAllowed(animal, { x: state.x, y: state.y })) {
        this.resetAnimalToAllowedPoint(animal, state, config, now);
        return;
      }

      state.floatOffset =
        animal.motion === "wander"
          ? 0
          : Math.sin(now / 420 + animal.delay) * 0.45;
      const dx = state.targetX - state.x;
      const dy = state.targetY - state.y;
      const distance = Math.hypot(dx, dy);

      if (distance < 0.7 || now >= state.nextTargetAt) {
        this.pickAnimalTarget(animal, state, now);
        return;
      }

      const step = Math.min(distance, config.speed * elapsedSeconds);
      const nextPoint = {
        x: state.x + (dx / distance) * step,
        y: state.y + (dy / distance) * step,
      };

      if (!isAnimalPointAllowed(animal, nextPoint)) {
        this.pickAnimalTarget(animal, state, now);
        return;
      }

      state.x = nextPoint.x;
      state.y = nextPoint.y;
    });

    this.changeDetector.detectChanges();
  }

  private async loadCrewGateData() {
    this.crewGateBlockedIslandIds = new Set<string>();
    if (!this.crewId || this.islands.length <= 1) return;

    const { data: crewData } = await backend
      .from("crews")
      .select("owner_id")
      .eq("id", this.crewId)
      .maybeSingle();
    const { data: membersData } = await backend
      .from("crew_members")
      .select("user_id")
      .eq("crew_id", this.crewId);
    const memberIds = Array.from(
      new Set(
        [
          crewData?.owner_id,
          ...((membersData as { user_id: string }[] | null) || []).map(
            (member) => member.user_id,
          ),
        ].filter(Boolean) as string[],
      ),
    );

    if (memberIds.length === 0) return;

    const { data: progressData } = await backend
      .from("island_progress")
      .select("user_id, island_id")
      .in("user_id", memberIds);
    const completedOrdersByUser = new Map<string, Set<number>>();

    (
      (progressData as { user_id: string; island_id: string }[] | null) || []
    ).forEach((progress) => {
      const orderIndex = this.allIslandOrderById.get(progress.island_id);
      if (orderIndex === undefined) return;

      const completedOrders =
        completedOrdersByUser.get(progress.user_id) || new Set<number>();
      completedOrders.add(orderIndex);
      completedOrdersByUser.set(progress.user_id, completedOrders);
    });

    const blocked = new Set<string>();
    this.islands.forEach((island, index) => {
      if (index === 0) return;

      const previousOrder = this.islands[index - 1].order_index;
      const everyoneReady = memberIds.every((memberId) =>
        completedOrdersByUser.get(memberId)?.has(previousOrder),
      );
      if (!everyoneReady) blocked.add(island.id);
    });

    this.crewGateBlockedIslandIds = blocked;
  }

  private async loadPendingCrewInvitationCount() {
    if (!this.user?.email) {
      this.pendingCrewInvitationCount = 0;
      return;
    }

    const { data, error } = await backend
      .from("crew_invitations")
      .select("id, expires_at")
      .eq("invited_email", this.user.email.trim().toLowerCase())
      .eq("status", "pending");

    if (error) {
      console.warn("Could not load pending crew invitations:", error);
      return;
    }

    const now = Date.now();
    const invitations = (
      (data || []) as { id: string; expires_at: string }[]
    ).filter((invitation) => {
      const expiresAt = new Date(invitation.expires_at).getTime();
      return Number.isFinite(expiresAt) && expiresAt > now;
    });
    this.pendingCrewInvitationCount = invitations.length;
    this.notifyAboutNewCrewInvitations(invitations.map((item) => item.id));
  }

  private notifyAboutNewCrewInvitations(invitationIds: string[]) {
    if (!this.user?.id || invitationIds.length === 0) return;

    const storageKey = `${CREW_INVITE_NOTIFICATION_KEY_PREFIX}${this.user.id}`;
    const seenIds = this.readNotifiedCrewInvitationIds(storageKey);
    const newInvitationIds = invitationIds.filter((id) => !seenIds.has(id));
    if (newInvitationIds.length === 0) return;

    this.toast.info(
      this.language.t("map.newCrewInviteToast", {
        count: newInvitationIds.length,
      }),
    );
    newInvitationIds.forEach((id) => seenIds.add(id));
    this.writeNotifiedCrewInvitationIds(storageKey, seenIds);
  }

  private readNotifiedCrewInvitationIds(storageKey: string) {
    try {
      const rawValue = window.localStorage.getItem(storageKey);
      const parsedValue = rawValue ? JSON.parse(rawValue) : [];
      return new Set<string>(
        Array.isArray(parsedValue)
          ? parsedValue.filter(
              (value): value is string => typeof value === "string",
            )
          : [],
      );
    } catch {
      return new Set<string>();
    }
  }

  private writeNotifiedCrewInvitationIds(
    storageKey: string,
    invitationIds: Set<string>,
  ) {
    try {
      window.localStorage.setItem(
        storageKey,
        JSON.stringify([...invitationIds]),
      );
    } catch {
      // Notification persistence is best effort only.
    }
  }

  private isCompactMapViewport() {
    return (
      typeof window !== "undefined" &&
      window.innerWidth < COMPACT_MAP_BREAKPOINT_PX
    );
  }

  mobileIslandStatusLabel(island: Island, index: number) {
    if (this.completedIslandIds.has(island.id))
      return this.language.t("common.completed");
    if (island.id === this.currentIslandId)
      return this.language.t("common.current");
    if (this.isIslandLocked(index)) return this.language.t("common.locked");
    return this.language.t("island.beginJourney");
  }

  mobileIslandRowClass(island: Island, index: number) {
    return cn(
      "flex w-full items-center gap-3 rounded-md border bg-card/95 p-3 text-left shadow-sm transition active:scale-[0.99]",
      this.completedIslandIds.has(island.id) &&
        "border-emerald-200 bg-emerald-50/95",
      island.id === this.currentIslandId &&
        !this.completedIslandIds.has(island.id) &&
        "border-primary/40 bg-primary/10 shadow-md",
      this.isIslandLocked(index) &&
        island.id !== this.currentIslandId &&
        !this.completedIslandIds.has(island.id) &&
        "border-border bg-card/70 opacity-85",
      !this.isIslandLocked(index) &&
        island.id !== this.currentIslandId &&
        !this.completedIslandIds.has(island.id) &&
        "border-amber-200 bg-amber-50/90",
    );
  }

  mobileIslandNumberClass(island: Island, index: number) {
    return cn(
      "grid h-11 w-11 shrink-0 place-items-center rounded-full border-2 text-sm font-black shadow-sm",
      this.completedIslandIds.has(island.id) &&
        "border-emerald-200 bg-emerald-500 text-white",
      island.id === this.currentIslandId &&
        !this.completedIslandIds.has(island.id) &&
        "border-white bg-primary text-primary-foreground",
      this.isIslandLocked(index) &&
        island.id !== this.currentIslandId &&
        !this.completedIslandIds.has(island.id) &&
        "border-slate-200 bg-slate-200 text-slate-500",
      !this.isIslandLocked(index) &&
        island.id !== this.currentIslandId &&
        !this.completedIslandIds.has(island.id) &&
        "border-amber-200 bg-amber-100 text-amber-950",
    );
  }

  buttonClasses = buttonClasses;
  cardClasses = cardClasses;
  badgeClasses = badgeClasses;
  progressBarStyle = progressBarStyle;
}
